/* ─────────────────────────────────────────────────────────────────────────
   THEME JS · Horizon Editions
   SI-17-B · Launch-blocker interaction fixes.

   Implements the two JS-side interaction blockers identified in SI-17-A:

     1. Mobile menu toggle
        — [data-mobile-menu-toggle] click toggles aria-expanded on the button
          and aria-hidden on [data-mobile-menu]. Existing CSS rules
          .mobile-menu[aria-hidden="true|false"] handle the visual state and
          the visibility:hidden contract that takes the menu out of tab order.

     2. Cart drawer toggle
        — [data-cart-drawer-toggle] is an <a href="/cart"> in the header. On
          click we preventDefault and toggle the drawer. If JS is unavailable
          or this script hasn't loaded, the anchor still navigates to /cart
          (graceful fallback).
        — [data-cart-drawer-close] + [data-cart-drawer-backdrop] close.
        — Escape closes either overlay when open.
        — We also set the `inert` attribute on the closed drawer so its
          descendants stay out of the tab order (the closed drawer uses
          transform:translateX(100%) — not visibility:hidden — so without
          inert, its links remain Tab-focusable off-screen).
        — Subscribes to the Dawn pubsub PUB_SUB_EVENTS.cartUpdate event so
          add-to-cart on a PDP auto-opens the drawer. Guarded — no-ops if
          pubsub helpers aren't present.

   Focus management:
     On open, remember document.activeElement; move focus to the first
     focusable in the overlay. On close, restore focus to the remembered
     element (falls back to the trigger if the saved element is gone).

   No external dependencies. No theme-file edits other than this asset.
   ───────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /**
   * First tab-focusable descendant of a container, or null.
   */
  function firstFocusable(container) {
    if (!container) return null;
    var sel = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    return container.querySelector(sel);
  }

  /**
   * Safely call .focus on an element if it has a focus method.
   */
  function safeFocus(el) {
    if (el && typeof el.focus === 'function') {
      try { el.focus({ preventScroll: true }); }
      catch (e) { try { el.focus(); } catch (e2) {} }
    }
  }

  /* ─── Module A · Mobile menu toggle ───────────────────────────── */
  /* addendum 6 §2 · which control produced the add. The reversal condition
     in the main task turns on this field: if the sticky bar records zero
     add-to-carts over a meaningful number of sessions, the problem is the
     offer or the traffic, not the layout — and that cannot be evaluated
     without knowing which control was used. Set at click time, read after
     the POST succeeds. */
  var heAtcVia = 'panel';

  function initMobileMenu() {
    var toggle = document.querySelector('[data-mobile-menu-toggle]');
    var menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;

    var lastFocused = null;

    function isOpen() {
      return toggle.getAttribute('aria-expanded') === 'true';
    }
    function open() {
      lastFocused = document.activeElement;
      toggle.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');
      safeFocus(firstFocusable(menu));
    }
    function close() {
      toggle.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
      safeFocus(lastFocused || toggle);
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      if (isOpen()) close(); else open();
    });

    // Click any anchor inside the menu → close (we're navigating away).
    // Click the explicit X close button → close. The X carries
    // `data-mobile-menu-close` to keep the open toggle (hamburger,
    // `data-mobile-menu-toggle`) and the close action cleanly separated.
    menu.addEventListener('click', function (e) {
      if (e.target.closest('a[href]') || e.target.closest('[data-mobile-menu-close]')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });
  }

  /* ─── Module B · Cart drawer toggle ───────────────────────────── */
  function initCartDrawer() {
    var triggers = Array.from(document.querySelectorAll('[data-cart-drawer-toggle]'));
    var drawer = document.querySelector('[data-cart-drawer], #CartDrawer');
    if (!drawer || triggers.length === 0) return;

    var lastFocused = null;

    // Closed drawer uses transform:translateX(100%) — its descendants stay
    // tab-focusable off-screen. Set the `inert` attribute so they don't.
    function setInert(state) {
      try {
        if (state) drawer.setAttribute('inert', '');
        else drawer.removeAttribute('inert');
      } catch (e) {}
    }
    if (drawer.getAttribute('aria-hidden') !== 'false') setInert(true);

    function isOpen() {
      return drawer.getAttribute('aria-hidden') === 'false';
    }
    // addendum 2 §1 · iOS Safari ignores `body { overflow: hidden }` for touch
    // and scrolls the page anyway to keep a focused field in view — with the
    // keyboard up, the PDP showed underneath the drawer. Position-fixing the
    // body is what actually holds it: the scroll position is saved, the body
    // is pinned at a negative offset, and the position is restored on close so
    // the buyer lands exactly where they were.
    var scrollLockY = 0;
    function lockScroll() {
      scrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + scrollLockY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
    function unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollLockY);
    }

    function open() {
      lastFocused = document.activeElement;
      lockScroll();
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      drawer.setAttribute('aria-hidden', 'false');
      // PDP-CART-DRAWER-CANONICAL-ALIGN-1 · Phase 3 · keep
      // data-cart-state in sync with aria-hidden so canonical CSS
      // selectors (per 20_CART_BEHAVIOR_SPEC §4.3) can target it.
      drawer.setAttribute('data-cart-state', 'open');
      setInert(false);
      safeFocus(firstFocusable(drawer));
    }
    function close() {
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('data-cart-state', 'closed');
      setInert(true);
      unlockScroll();
      safeFocus(lastFocused);
    }

    // Triggers — preventDefault so the <a href="/cart"> doesn't navigate.
    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        if (isOpen()) close(); else open();
      });
    });

    // Close buttons inside the drawer.
    //
    // PDP-CART-DRAWER-ATC-QA-FIX-1 · Extracted into a re-callable
    // idempotent helper so it can run again after innerHTML swaps
    // performed by `initCartLine.rerenderFromSections` (cart line
    // qty/remove) and Module F `initBuyPanelAtcAjax` (PDP ATC). Both
    // of those swaps replace drawer children; the close button is the
    // only inner control bound DIRECTLY (not via event delegation),
    // so without this helper the close button dies on every swap.
    // Idempotency via element property `__closeWired`.
    function wireCloseButtons() {
      drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (b) {
        if (b.__closeWired === true) return;
        b.__closeWired = true;
        b.addEventListener('click', function (e) { e.preventDefault(); close(); });
      });
    }
    wireCloseButtons();
    drawer.__rewireCloseButtons = wireCloseButtons;
    // PDP-RESKIN-ATC-DRAWER-NO-OPEN hotfix · expose open/close so external
    // modules (Module E PDP ATC intercept) can drive the drawer without
    // relying on the broken pubsub path. `window.PUB_SUB_EVENTS` is a
    // `const` in constants.js — not a window property — so the
    // `cartUpdate` publish + the cart-drawer's own pubsub subscriber are
    // both silent no-ops. The cart-icon trigger uses its own click listener
    // (this same `open` function captured at line ~145 below), so that
    // path stays intact. This new hook lets Module E call the SAME `open`
    // after a successful /cart/add.js.
    drawer.__open = open;
    drawer.__close = close;
    drawer.__isOpen = isOpen;

    // Backdrop click closes (backdrop element lives outside the aside).
    // Element-bound listener for the initial render…
    document.querySelectorAll('[data-cart-drawer-backdrop]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });
    // …plus PHASE-2C · defensive document-level delegation as a fail-safe.
    // Survives any future replacement of the backdrop DOM node (section-group
    // re-renders, theme-editor swaps, etc.) without needing to re-wire.
    if (!document.__heCartDrawerBackdropDelegated) {
      document.__heCartDrawerBackdropDelegated = true;
      document.addEventListener('click', function (e) {
        if (!isOpen()) return;
        var bd = e.target.closest && e.target.closest('[data-cart-drawer-backdrop]');
        if (bd) { e.preventDefault(); close(); }
      });
    }

    // Escape closes when open.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });

    // Subscribe to Dawn's cart-update pubsub so add-to-cart auto-opens.
    // pubsub.js exposes window.subscribe and window.PUB_SUB_EVENTS. Guarded.
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        if (!isOpen()) open();
      });
    }

    // PDP-CART-DRAWER-UPSELL-PIVOT-1 · single boot log for sanity-check.
    try { console.log('[cart-drawer] initialized'); } catch (e) {}
  }

  /* ─── Module C · PDP variant sync (SI-17-C) ────────────────────
     Wires the variant picker (radios outside the form) to the
     <form action="/cart/add"> hidden id input. Without this module
     the form would submit the default variant regardless of which
     color/size the customer selected (QA-2 finding).

     Per-PDP data source: a JSON island emitted by snippets/buy-panel.liquid
     <script type="application/json" data-product-variants-json>[…]</script>
     containing the product's variants (id, available, price,
     compare_at_price, title, options[]).

     No hardcoded variant IDs, no product-handle dependency. No-ops
     gracefully when any hook (picker, form, hidden id) is missing.
     Premiere / Master Set PDPs that use a single default variant
     simply don't emit the JSON island and the module skips them.
     ─────────────────────────────────────────────────────────────── */
  function initVariantSync() {
    var nodes = document.querySelectorAll('[data-product-variants-json]');
    if (!nodes.length) return;

    nodes.forEach(function (jsonNode) {
      var variants;
      try { variants = JSON.parse((jsonNode.textContent || '').trim()); }
      catch (e) { return; }
      if (!Array.isArray(variants) || variants.length === 0) return;

      // Locate the .pdp-buy container that holds both the picker and the form.
      var pdpBuy = jsonNode.closest('.pdp-buy') || jsonNode.closest('[data-pdp-buy]');
      if (!pdpBuy) return;

      var picker = pdpBuy.querySelector('[data-variant-picker]');
      var form = pdpBuy.querySelector('form[action*="/cart/add"]');
      if (!picker || !form) return;

      var hiddenId = form.querySelector('input[name="id"]');
      var hiddenTeeColor = form.querySelector('input[name="properties[_tee_color]"]');
      var hiddenTeeSize = form.querySelector('input[name="properties[_tee_size]"]');
      var atcBtn = form.querySelector('button[name="add"], button[type="submit"]');
      var priceCurrent = pdpBuy.querySelector('.buy-panel__price-current');
      var priceCompare = pdpBuy.querySelector('.buy-panel__price-compare');
      var priceSavings = pdpBuy.querySelector('.buy-panel__price-savings');
      var stickyPrice = document.querySelector('[data-sticky-atc-price]');
      // PRICE-TIER-FIX-2 · the savings badge rendered by buy-panel.liquid is
      // `.pdp-buy__save`. `.buy-panel__price-savings` above is a legacy class
      // that no longer exists in the markup, so the badge was never recomputed
      // and kept claiming the default variant's savings at every size tier.
      var priceSave = pdpBuy.querySelector('.pdp-buy__save');
      var priceRow = pdpBuy.querySelector('.pdp-buy__price-row');
      var panelFormat = pdpBuy.getAttribute('data-format') || '';
      // PRICE-TIER-FIX-3 · the spec band's "· save $X" clause is rendered by
      // buy-panel.liquid into its own span so it can track the selected
      // variant instead of repeating a hardcoded figure at every tier.
      var specBandSave = pdpBuy.querySelector('[data-spec-band-save]');
      // PRICE-TIER-FIX-1 · the mobile sticky bar renders a second ATC button
      // (snippets/buy-button.liquid) whose label embeds the price. Only the
      // sticky *price span* was being updated, so the button label went stale.
      var stickyBar = document.querySelector('[data-sticky-atc]');
      // PDP-MOBILE-3 · the bar now carries the panel's own `.atc` button
      // ([data-sticky-atc-btn]); [data-buy-button] remains as the fallback for
      // any surface still rendering the older buy-button snippet.
      var stickyBtn = stickyBar
        ? (stickyBar.querySelector('[data-sticky-atc-btn]') || stickyBar.querySelector('[data-buy-button]'))
        : null;
      var stickyOriginalText = stickyBtn ? (stickyBtn.textContent || '').trim() : '';
      // Capture the initial (default-variant) ATC label so we can restore
      // CTA wording for Premiere/Master tiers — those use Liquid-rendered
      // tier-aware copy that we do not want to overwrite.
      var atcOriginalText = atcBtn ? (atcBtn.textContent || '').trim() : '';
      // PRICE-TIER-FIX-1 · buy-panel.liquid renders the ATC button without a
      // data-format attribute, so this was always '' and updateAtc() fell
      // through to the "restore the original Liquid label" branch — freezing
      // the button at the default variant's price. The panel wrapper
      // (.pdp-buy[data-format]) always carries the format, so fall back to it.
      var atcFormat = (atcBtn ? (atcBtn.getAttribute('data-format') || '') : '') || panelFormat;

      function getSelectedOptions() {
        var checked = Array.prototype.slice.call(
          picker.querySelectorAll('input[data-variant-option]:checked')
        );
        checked.sort(function (a, b) {
          return (parseInt(a.dataset.optionPosition, 10) || 0) -
                 (parseInt(b.dataset.optionPosition, 10) || 0);
        });
        return checked.map(function (r) { return r.value; });
      }

      function findVariant(selected) {
        if (!selected.length) return null;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (!v || !v.options || v.options.length !== selected.length) continue;
          var match = true;
          for (var j = 0; j < v.options.length; j++) {
            if (v.options[j] !== selected[j]) { match = false; break; }
          }
          if (match) return v;
        }
        return null;
      }

      // Shopify variant.price is integer cents (e.g. 4500 = $45.00).
      // PDP-BUY-PANEL-IMPL-1B / Fix #1 · strip " USD" and ".00" for whole-
      // dollar visual prices; preserve non-zero cents (e.g. $45.50).
      // Matches the Liquid price-display helper in buy-panel.liquid +
      // format-selector.liquid. Visual only — Shopify prices / JSON-LD /
      // cart / checkout remain untouched.
      function formatMoney(cents) {
        if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
        var dollars = Math.floor(cents / 100);
        var remainder = cents % 100;
        if (remainder === 0) return '$' + dollars;
        var pad = remainder < 10 ? '0' + remainder : '' + remainder;
        return '$' + dollars + '.' + pad;
      }

      // PRICE-TIER-FIX-1 · one label rule for every ATC button on the PDP:
      // the in-panel button and the mobile sticky-bar button, which carries
      // its own Liquid-baked price label (snippets/buy-button.liquid) and
      // went stale in exactly the same way.
      function applyAtcLabel(btn, originalText, variant) {
        if (!btn) return;
        if (!variant.available) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.textContent = 'Sold out';
          return;
        }
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.setAttribute('data-variant-id', variant.id);

        // Only rewrite CTA copy for Archive tiers (tee/print/complete).
        // Premiere/Master copy is tier-aware and rendered by buy-button Liquid.
        if (atcFormat === 'tee' || atcFormat === 'print') {
          btn.textContent = 'Add to cart · ' + formatMoney(variant.price);
        } else if (atcFormat === 'complete') {
          btn.textContent = 'Add Complete Edition · ' + formatMoney(variant.price);
        } else if (originalText && btn.textContent.trim() !== originalText) {
          // QW2.2 · ATC buttons without a data-format hint: restore the original
          // Liquid label once a valid variant is chosen (undo "Select a size").
          btn.textContent = originalText;
        }
        // For premiere/master, leave btn.textContent at Liquid-rendered value.
      }

      function updateAtc(variant) {
        applyAtcLabel(atcBtn, atcOriginalText, variant);
        applyAtcLabel(stickyBtn, stickyOriginalText, variant);
      }

      function updatePriceDisplay(variant) {
        if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);
        if (priceCompare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceCompare.textContent = formatMoney(variant.compare_at_price);
            priceCompare.style.display = '';
            if (priceSavings) {
              priceSavings.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
              priceSavings.style.display = '';
            }
          } else {
            priceCompare.style.display = 'none';
            if (priceSavings) priceSavings.style.display = 'none';
          }
        }
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        updateSavingsBadge(variant);
        updateSpecBandSaving(variant);
      }

      // PRICE-TIER-FIX-2 · recompute the "Save $X" badge from the selected
      // variant. Real savings exist only when compare_at_price > price; at
      // tiers where they are equal (e.g. Complete Edition 3XL/4XL, where
      // compareAtPrice === price) the badge is hidden rather than left
      // showing the base tier's figure. Badge creation stays limited to
      // Complete PDPs — the only format whose Liquid renders this badge.
      function updateSavingsBadge(variant) {
        var save = 0;
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          save = variant.compare_at_price - variant.price;
        }
        // PDP-MOBILE-6 · on a Complete PDP the edition chip shows the saving
        // for the selected size and disappears where there is none (3XL, where
        // compare_at_price equals price). On tee and print the chip is the
        // upsell to the Complete Edition and is left exactly as Liquid rendered it.
        if (panelFormat === 'complete') {
          var editionSave = pdpBuy.querySelector('[data-edition-save]');
          if (editionSave) {
            if (save > 0) {
              editionSave.textContent = 'Save ' + formatMoney(save);
              editionSave.hidden = false;
            } else {
              editionSave.hidden = true;
            }
          }
        }
        if (!priceSave) {
          if (save <= 0 || !priceRow || panelFormat !== 'complete') return;
          priceSave = document.createElement('span');
          priceSave.className = 'pdp-buy__save';
          priceRow.appendChild(priceSave);
        }
        if (save > 0) {
          priceSave.textContent = 'Save ' + formatMoney(save);
          priceSave.style.display = '';
        } else {
          priceSave.style.display = 'none';
        }
      }

      // PRICE-TIER-FIX-3 · the spec band on Complete PDPs used to end in a
      // hardcoded "· save $11" regardless of the selected size. Recompute it
      // from the same compare_at_price − price maths as the badge and drop
      // the clause entirely when the selected variant has no real saving.
      function updateSpecBandSaving(variant) {
        if (!specBandSave) return;
        var save = 0;
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          save = variant.compare_at_price - variant.price;
        }
        specBandSave.textContent = save > 0 ? ' · save ' + formatMoney(save) : '';
      }

      // PDP-BUY-PANEL-IMPL-1B / Fix #4 · update the .pdp-buy__label-value
      // span inside the color fieldset so the legend reads
      //   "Colorway · {selected value}"
      // when the user picks a different color radio. Legend initial value
      // is rendered by Liquid; this keeps it in sync with the live radio
      // selection. No-op on PDPs without a color fieldset.
      function updateColorLabel() {
        var colorFieldset = picker.querySelector('.pdp-buy__group--color');
        if (!colorFieldset) return;
        var labelValue = colorFieldset.querySelector('.pdp-buy__label-value');
        var checkedRadio = colorFieldset.querySelector('input[data-variant-option]:checked');
        if (!checkedRadio) return;
        var legend = colorFieldset.querySelector('legend.pdp-buy__label');
        if (!legend) return;
        if (labelValue) {
          labelValue.textContent = '· ' + checkedRadio.value;
        } else {
          // Legend was rendered without an initial value span (selected_value
          // was blank at render time). Append one now so subsequent updates
          // have a node to target.
          var span = document.createElement('span');
          span.className = 'pdp-buy__label-value';
          span.textContent = ' · ' + checkedRadio.value;
          legend.appendChild(span);
        }
      }

      // PDP-BUY-PANEL-TEE-AVAILABILITY-PATCH · Color-drives-size +
      // auto-pivot. Replaces the IMPL-2 bidirectional pattern (which
      // disabled colors when the current size made them invalid —
      // locking the user out of Oxblood/Forest if XS was selected).
      //
      // New behavior for Color × Size pickers (Tee canonical layout):
      //   - Color radios stay enabled unless a color has zero
      //     available variants (defensive — never fires in current
      //     Half Dome Tee data; reserved for full-inventory-outage
      //     edge cases).
      //   - Size radios disable when (currentColor, thisSize) has no
      //     available Shopify variant. Driven purely by JSON island
      //     data, no hardcoded availability lists.
      //   - When the currently-checked size becomes disabled after a
      //     color change, auto-pivot the size to the first non-disabled
      //     radio in visual order. Set `checked` directly (without
      //     dispatching a synthetic event — avoids recursion). Caller
      //     `sync()` re-reads `getSelectedOptions()` after this
      //     function returns, so the post-pivot variant is what
      //     drives the hidden id + ATC + price.
      //
      // Single-option layouts (defensive — Print PDP currently emits
      // no picker, but if a future product has a 1-option picker the
      // original bidirectional behavior is correct).
      //
      // Other multi-option layouts (defensive — no current product
      // matches): fall back to the original bidirectional pattern.
      function updateAvailability() {
        var fieldsets = picker.querySelectorAll('fieldset.pdp-buy__group[data-option-index]');
        if (!fieldsets.length) return;
        var selected = getSelectedOptions();
        // QW2.2 · run with a partial selection (colour chosen, size not yet) so
        // sizes with no variant for the chosen colour disable before a size is picked.
        if (selected.length === 0) return;

        // PHASE-2D · resilient color/size detection · the Stage 4 picker markup
        // emits bare `.pdp-buy__group` fieldsets (no --color/--size modifier
        // classes), so the legacy class-modifier selectors below never matched.
        // Detect role from the radio's name attribute (`options[<opt name>]`)
        // — covers "Color", "Tee Color", "Size", "Tee Size", "Print Size".
        function _roleOfFieldset(fs) {
          var r = fs.querySelector('input[data-variant-option]');
          if (!r) return null;
          var n = (r.name || '').toLowerCase();
          if (n.indexOf('color') >= 0) return 'color';
          if (n.indexOf('size')  >= 0) return 'size';
          return null;
        }
        var colorFs = picker.querySelector('.pdp-buy__group--color');
        var sizeFs  = picker.querySelector('.pdp-buy__group--size');
        if (!colorFs || !sizeFs) {
          for (var fff = 0; fff < fieldsets.length; fff++) {
            var role = _roleOfFieldset(fieldsets[fff]);
            if (role === 'color' && !colorFs) colorFs = fieldsets[fff];
            else if (role === 'size' && !sizeFs) sizeFs = fieldsets[fff];
          }
        }

        // PHASE-2D · mirror disabled state from a hidden radio onto the visible
        // re-skin button(s). The Stage 4 markup uses [data-pdp-swatch] (color)
        // and [data-pdp-size] (size) buttons; mirroring keeps them in sync with
        // the hidden picker's availability so users see disabled sizes.
        function _mirrorDisabled(visBtnSelector, value, disabled) {
          var btns = pdpBuy.querySelectorAll(visBtnSelector);
          for (var b = 0; b < btns.length; b++) {
            if (btns[b].getAttribute('data-option-value') !== value) continue;
            if (disabled) {
              btns[b].setAttribute('disabled', '');
              btns[b].setAttribute('aria-disabled', 'true');
            } else {
              btns[b].removeAttribute('disabled');
              btns[b].removeAttribute('aria-disabled');
            }
          }
        }
        function _mirrorPressed(visBtnSelector, optName, newValue) {
          var btns = pdpBuy.querySelectorAll(visBtnSelector);
          for (var b = 0; b < btns.length; b++) {
            if (optName && btns[b].getAttribute('data-option-name') !== optName) continue;
            btns[b].setAttribute('aria-pressed', btns[b].getAttribute('data-option-value') === newValue ? 'true' : 'false');
          }
        }

        // Single-option product (only size, only color, or any single
        // axis): original per-radio bidirectional check works.
        if (fieldsets.length === 1) {
          var only = fieldsets[0];
          var onlyIdx = parseInt(only.dataset.optionIndex, 10);
          if (isNaN(onlyIdx)) return;
          var onlyRadios = only.querySelectorAll('input[data-variant-option]');
          for (var oi = 0; oi < onlyRadios.length; oi++) {
            var or = onlyRadios[oi];
            var tentativeS = selected.slice();
            tentativeS[onlyIdx] = or.value;
            var vS = findVariant(tentativeS);
            or.disabled = !vS || !vS.available;
          }
          return;
        }

        // Canonical Color × Size product (Tee).
        if (colorFs && sizeFs) {
          var colorIdx = parseInt(colorFs.dataset.optionIndex, 10);
          var sizeIdx = parseInt(sizeFs.dataset.optionIndex, 10);
          if (isNaN(colorIdx) || isNaN(sizeIdx)) return;

          // 1. Color radios — disable only if NO variant exists with
          //    this color available (defensive against full-color
          //    outage; otherwise color stays open).
          var colorRadios = colorFs.querySelectorAll('input[data-variant-option]');
          for (var ci = 0; ci < colorRadios.length; ci++) {
            var cr = colorRadios[ci];
            var anyAvailable = false;
            for (var vi = 0; vi < variants.length; vi++) {
              var vv = variants[vi];
              if (!vv || !vv.available || !vv.options) continue;
              if (vv.options[colorIdx] === cr.value) { anyAvailable = true; break; }
            }
            cr.disabled = !anyAvailable;
            _mirrorDisabled('[data-pdp-swatch]', cr.value, cr.disabled);
          }

          // 2. Size radios — disable (currentColor, thisSize) combos
          //    that have no available Shopify variant.
          var currentColor = selected[colorIdx];
          var sizeRadios = sizeFs.querySelectorAll('input[data-variant-option]');
          for (var si = 0; si < sizeRadios.length; si++) {
            var sr = sizeRadios[si];
            var tentative = selected.slice();
            tentative[colorIdx] = currentColor;
            tentative[sizeIdx] = sr.value;
            var found = findVariant(tentative);
            sr.disabled = !found || !found.available;
            _mirrorDisabled('[data-pdp-size]', sr.value, sr.disabled);
          }

          // 3. QW2.2 · NO silent auto-pivot. If the currently-checked size is
          //    no longer valid for the new colour, DESELECT it — the customer
          //    must actively pick a size (a missing size must never be swapped
          //    for another). ATC stays gated until they choose a valid size.
          var checkedSize = sizeFs.querySelector('input[type="radio"]:checked');
          if (checkedSize && checkedSize.disabled) {
            checkedSize.checked = false;
            var _sizeOptName = (checkedSize.name || '').replace(/^options\[/, '').replace(/\]$/, '');
            _mirrorPressed('[data-pdp-size]', _sizeOptName, null);
          }
          return;
        }

        // Other multi-option layouts — bidirectional fallback.
        for (var f = 0; f < fieldsets.length; f++) {
          var fs = fieldsets[f];
          var optIdx = parseInt(fs.dataset.optionIndex, 10);
          if (isNaN(optIdx)) continue;
          var radiosF = fs.querySelectorAll('input[data-variant-option]');
          for (var rf = 0; rf < radiosF.length; rf++) {
            var rrF = radiosF[rf];
            var tentativeF = selected.slice();
            tentativeF[optIdx] = rrF.value;
            var vF = findVariant(tentativeF);
            rrF.disabled = !vF || !vF.available;
          }
        }
      }

      function sync() {
        var selected = getSelectedOptions();
        // PDP-BUY-PANEL-FIX-PRINT-CTA-POSTCTA · Defensive bail when
        // the picker has no `data-variant-option` radios checked. This
        // happens on PDPs that ship a JSON island but no interactive
        // picker (e.g. when option fieldsets are conditionally
        // suppressed). In that case Liquid is authoritative for the
        // variant, price, and CTA state — do nothing here. Without
        // this bail, `findVariant([])` returns null and the next
        // branch disables the CTA on first paint.
        if (selected.length === 0) {
          updateColorLabel();
          return;
        }
        // PDP-BUY-PANEL-TEE-AVAILABILITY-PATCH · Run availability +
        // auto-pivot BEFORE variant lookup so the hidden id reflects
        // the post-pivot selection. Without this re-read, a color
        // change that pivots size would leave hidden id pointing at
        // the pre-pivot (invalid) combination.
        updateAvailability();
        selected = getSelectedOptions();
        var variant = findVariant(selected);
        if (!variant) {
          // PDP-MOBILE-4 · on mobile the button is never disabled. A disabled
          // button reading "Select a size" took zero taps in 50 sessions; the
          // button now stays live and opens the size sheet instead. The label
          // carries the price of the cheapest available variant for the
          // current colour, so it is never blank or stale.
          if (atcBtn && window.HEPdpMobile && window.HEPdpMobile.isMobile()) {
            atcBtn.disabled = false;
            atcBtn.removeAttribute('aria-disabled');
            var _fallback = window.HEPdpMobile.cheapestVariant(variants, getSelectedOptions());
            if (_fallback) applyAtcLabel(atcBtn, atcOriginalText, _fallback);
            if (_fallback && stickyBtn) applyAtcLabel(stickyBtn, stickyOriginalText, _fallback);
            updateColorLabel();
            return;
          }
          if (atcBtn) {
            atcBtn.disabled = true;
            atcBtn.setAttribute('aria-disabled', 'true');
            // QW2.2 · no valid variant for the current selection (size not
            // chosen, or the chosen size doesn't exist for this colour) — if
            // this product has a size axis, prompt for it. Detected from the
            // option name (not data-format, which this ATC button omits).
            var _hasSizeAxis = false;
            var _qfs = picker.querySelectorAll('fieldset.pdp-buy__group[data-option-index]');
            for (var _qi = 0; _qi < _qfs.length; _qi++) {
              var _qr = _qfs[_qi].querySelector('input[data-variant-option]');
              if (_qr && (_qr.name || '').toLowerCase().indexOf('size') >= 0) { _hasSizeAxis = true; break; }
            }
            if (_hasSizeAxis) atcBtn.textContent = 'Select a size';
          }
          updateColorLabel();
          return;
        }
        if (hiddenId) hiddenId.value = variant.id;
        // TEE-PROPS-SYNC · keep cart line-item properties in sync with the
        // selected variant. Liquid renders these hidden inputs with the
        // default variant's option1/option2 (color/size); without this
        // mirror, every ATC sends the default values to fulfillment /
        // analytics / Klaviyo regardless of what the user picked.
        if (hiddenTeeColor && variant.option1 != null) hiddenTeeColor.value = variant.option1;
        if (hiddenTeeSize && variant.option2 != null) hiddenTeeSize.value = variant.option2;
        updateAtc(variant);
        updatePriceDisplay(variant);
        updateColorLabel();
      }

      picker.addEventListener('change', sync);
      // Initial run — if Shopify resolved a ?variant=ID URL param, this
      // syncs the UI to the resolved variant's data.
      sync();
    });
  }

  /* ─── Module D · Cart line handlers (CART-2-A) ────────────────────
     Wires the drawer's qty inc/dec and remove buttons to /cart/change.js.
     Uses Shopify's Section Rendering API to re-render the drawer body
     in place after each change, so totals/subtotals/empty-state all
     update without a page reload.

     Event delegation on the drawer container keeps handlers attached
     across innerHTML swaps — no manual rebind needed.

     Markup contract (sections/cart-drawer.liquid):
       [data-cart-drawer]                    drawer aside
       [data-section-id="cart-drawer"]       section id for Section Render API
       [data-cart-qty="inc"][data-key="…"]   increase button
       [data-cart-qty="dec"][data-key="…"]   decrease button (qty=0 removes)
       [data-cart-remove="…"]                remove button
       [data-qty-value]                      current quantity span
       [data-cart-status] (role=status,      live region for errors
                          aria-live=polite)
       [data-cart-count]                     count chips (drawer + header)
     ─────────────────────────────────────────────────────────────── */
  function initCartLine() {
    var drawer = document.querySelector('[data-cart-drawer], #CartDrawer');
    if (!drawer) return;

    // PDP-CART-DRAWER-RENDERING-ENDPOINT-FIX-2 · use the runtime instance
    // id (the `{{ section.id }}` value, e.g. `sections--27550012899509__cart-drawer`)
    // as the addressable id for the classic `/?section_id=<id>` endpoint.
    // SECTION-ID-FIX-1 attempted `/cart.js?sections=<addressable_name>` which
    // is non-functional on this theme — Shopify returned 200 but the response
    // never contained a `sections` key. The classic `?section_id=` endpoint
    // returns the rendered section HTML directly (text/html, NOT JSON) and
    // accepts the runtime instance id. The `data-section-name` attribute
    // (added in SECTION-ID-FIX-1) is now unused on this code path but is
    // left in place on the aside as a no-op until follow-up cleanup.
    var sectionId = drawer.getAttribute('data-section-id') || '';
    var inFlight = false;
    var statusTimer = null;

    function controls() {
      return drawer.querySelectorAll('[data-cart-qty], [data-cart-remove]');
    }
    function setBusy(busy) {
      // CART-2-B · Preserve Liquid-rendered persistent disabled state
      // (e.g. at-cap inc buttons for Premiere/Master Set). Only touch
      // `disabled` for buttons we ourselves disabled during in-flight.
      var btns = controls();
      for (var i = 0; i < btns.length; i++) {
        if (busy) {
          if (!btns[i].disabled) {
            btns[i].setAttribute('data-busy-touched', 'true');
            btns[i].disabled = true;
          }
          btns[i].setAttribute('aria-busy', 'true');
        } else {
          if (btns[i].getAttribute('data-busy-touched') === 'true') {
            btns[i].disabled = false;
            btns[i].removeAttribute('data-busy-touched');
          }
          btns[i].removeAttribute('aria-busy');
        }
      }
    }
    function clearStatus() {
      var region = drawer.querySelector('[data-cart-status]');
      if (region) region.textContent = '';
      if (statusTimer) { clearTimeout(statusTimer); statusTimer = null; }
    }
    function showStatus(msg) {
      var region = drawer.querySelector('[data-cart-status]');
      if (!region) return;
      region.textContent = msg;
      if (statusTimer) clearTimeout(statusTimer);
      statusTimer = setTimeout(function () {
        region.textContent = '';
        statusTimer = null;
      }, 5000);
    }

    // Update the header's data-cart-count chip(s) that live outside the drawer.
    function updateExternalCount(count) {
      var chips = document.querySelectorAll('[data-cart-count]');
      for (var i = 0; i < chips.length; i++) {
        if (!drawer.contains(chips[i])) {
          chips[i].textContent = count;
        }
      }
    }

    // Re-render drawer body in place from the section HTML payload.
    // PDP-CART-DRAWER-RENDERING-ENDPOINT-FIX-2 · signature changed: now
    // takes the raw HTML string returned by `GET /?section_id=<id>`
    // (text/html), not the `sections` object that the (broken) Cart API
    // section-render path used to return. Returns true on successful swap,
    // false otherwise — caller decides whether to surface an error.
    function rerenderFromHtml(html, fallbackCount, focusHint) {
      if (!html) {
        if (fallbackCount != null) updateExternalCount(fallbackCount);
        return false;
      }
      try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newDrawer = doc.querySelector('[data-cart-drawer], #CartDrawer');
        if (!newDrawer) {
          if (fallbackCount != null) updateExternalCount(fallbackCount);
          return false;
        }
        // Swap inner HTML of the drawer, preserving the outer <aside>
        // (and its handlers + aria-hidden/inert state).
        drawer.innerHTML = newDrawer.innerHTML;
        // PDP-CART-DRAWER-ATC-QA-FIX-1 · re-bind the close button (lives
        // INSIDE the drawer, bound directly not via delegation, so it
        // died with the innerHTML swap above).
        if (typeof drawer.__rewireCloseButtons === 'function') drawer.__rewireCloseButtons();
        // FIX-CART-01 · same reason as the close-button re-bind above —
        // the gift panel's toggle/message/email handlers live INSIDE
        // drawer.innerHTML and die on every swap. Re-running
        // initGiftPanel reattaches listeners to the freshly rendered
        // [data-cart-gift] nodes. Liquid renders cart-gift--active
        // from cart.attributes.gift, so the visual state is already
        // correct after the swap; this restores interactivity.
        initGiftPanel();
        // Update header count chips that aren't inside the drawer — read
        // fresh from the newly swapped DOM (preferred over fallbackCount).
        var newCountEl = drawer.querySelector('[data-cart-count]');
        if (newCountEl) {
          var newCount = newCountEl.textContent;
          var chips = document.querySelectorAll('[data-cart-count]');
          for (var i = 0; i < chips.length; i++) {
            if (!drawer.contains(chips[i])) chips[i].textContent = newCount;
          }
        } else if (fallbackCount != null) {
          updateExternalCount(fallbackCount);
        }
        // Restore focus sensibly after the swap.
        restoreFocus(focusHint);
        return true;
      } catch (e) {
        if (fallbackCount != null) updateExternalCount(fallbackCount);
        return false;
      }
    }

    // Try to restore focus to a logically-equivalent button after re-render.
    // focusHint: { kind: 'qty-inc'|'qty-dec'|'remove'|null, key: '…' }
    function restoreFocus(hint) {
      if (!hint || !hint.kind) return;
      var sel = null;
      if (hint.kind === 'qty-inc') {
        sel = '[data-cart-qty="inc"][data-key="' + hint.key + '"]';
      } else if (hint.kind === 'qty-dec') {
        sel = '[data-cart-qty="dec"][data-key="' + hint.key + '"]';
      } else if (hint.kind === 'remove') {
        sel = '[data-cart-remove="' + hint.key + '"]';
      }
      var target = sel ? drawer.querySelector(sel) : null;
      if (target && typeof target.focus === 'function') {
        try { target.focus({ preventScroll: true }); return; }
        catch (e) { try { target.focus(); return; } catch (e2) {} }
      }
      // Fallback: focus the close button (always present), or first focusable.
      var close = drawer.querySelector('[data-cart-drawer-close]');
      if (close && typeof close.focus === 'function') {
        try { close.focus({ preventScroll: true }); }
        catch (e) { try { close.focus(); } catch (e2) {} }
      }
    }

    async function change(line, quantity, focusHint) {
      if (inFlight || !line) return;
      inFlight = true;
      setBusy(true);
      clearStatus();
      try {
        // PDP-CART-DRAWER-RENDERING-ENDPOINT-FIX-2 · two-step pattern:
        //   STEP 1 · POST /cart/change.js (mutation only, no sections field)
        //   STEP 2 · GET /?section_id=<runtime_id> for fresh drawer HTML
        // The `/cart.js?sections=` endpoint is non-functional on this theme
        // (200 OK but no `sections` key in the response, regardless of name
        // format). The classic `/?section_id=` endpoint returns the rendered
        // section as text/html and works correctly with the runtime instance
        // id stored in `data-section-id`.
        var changeResp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: line, quantity: quantity })
        });
        if (!changeResp.ok) {
          var errBody = '';
          try { errBody = await changeResp.text(); } catch (e1) {}
          console.warn('[cart-drawer] POST /cart/change.js', changeResp.status, errBody);
          showStatus('Cart could not be updated.');
          return;
        }
        var cartData = null;
        try { cartData = await changeResp.json(); } catch (e2) {}

        if (!sectionId) {
          // Defensive: data-section-id missing on the drawer — can't render.
          // Fall back to count-only update from cartData if available.
          if (cartData && typeof cartData.item_count === 'number') {
            updateExternalCount(cartData.item_count);
          }
          return;
        }

        var renderResp = await fetch('/?section_id=' + encodeURIComponent(sectionId), {
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' }
        });
        if (!renderResp.ok) {
          console.warn('[cart-drawer] GET /?section_id=', renderResp.status);
          showStatus('Cart could not be updated.');
          return;
        }
        var html = await renderResp.text();
        var fallbackCount = cartData && typeof cartData.item_count === 'number'
          ? cartData.item_count
          : null;
        var rendered = rerenderFromHtml(html, fallbackCount, focusHint);
        if (!rendered) {
          showStatus('Cart could not be updated.');
          return;
        }
        // Publish for any other cart subscribers (e.g. header pill).
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-line', cartData: cartData });
        }
      } catch (e) {
        console.warn('[cart-drawer] change error', e);
        showStatus('Cart could not be updated.');
      } finally {
        inFlight = false;
        setBusy(false);
      }
    }

    // Event delegation — single handler survives drawer innerHTML swaps.
    drawer.addEventListener('click', function (e) {
      var inc = e.target.closest && e.target.closest('[data-cart-qty="inc"]');
      var dec = e.target.closest && e.target.closest('[data-cart-qty="dec"]');
      var rm = e.target.closest && e.target.closest('[data-cart-remove]');
      if (!inc && !dec && !rm) return;
      e.preventDefault();

      if (inc) {
        // CART-2-B · Honor max-quantity guard for Premiere/Master Set lines.
        // The Liquid renders the button as disabled when already at the cap,
        // but defense-in-depth: also guard here in case the disabled
        // attribute is stripped or markup is out of sync mid-swap.
        if (inc.disabled || inc.getAttribute('aria-disabled') === 'true') return;
        // PHASE-2D · BR-016 DROPPED. The cart-level gift-mode quantity-cap
        // guard (refuse [+] when data-gift-enabled="true") is removed —
        // gift lines may now hold qty > 1 freely.
        var keyI = inc.dataset.key;
        var cartItemI = inc.closest('.cart-item');
        var qtyI = parseInt((cartItemI && cartItemI.querySelector('[data-qty-value]') ? cartItemI.querySelector('[data-qty-value]').textContent : '0'), 10) || 0;
        var maxQ = parseInt(inc.getAttribute('data-max-quantity') || '0', 10) || 0;
        if (maxQ > 0 && qtyI >= maxQ) return;
        change(keyI, qtyI + 1, { kind: 'qty-inc', key: keyI });
      } else if (dec) {
        var keyD = dec.dataset.key;
        var cartItemD = dec.closest('.cart-item');
        var qtyD = parseInt((cartItemD && cartItemD.querySelector('[data-qty-value]') ? cartItemD.querySelector('[data-qty-value]').textContent : '0'), 10) || 0;
        var nextQty = Math.max(0, qtyD - 1);
        // If we'll remove the line, focus the next-most-sensible control after re-render.
        var hint = nextQty === 0 ? { kind: 'remove', key: null } : { kind: 'qty-dec', key: keyD };
        change(keyD, nextQty, hint);
      } else if (rm) {
        var keyR = rm.getAttribute('data-cart-remove');
        change(keyR, 0, { kind: 'remove', key: null });
      }
    });
  }

  /* ─── Module E · Complete PDP Tee sync (PDP-BUY-PANEL-COMPONENTS-2) ──
     Wires the Complete Edition PDP's line-item-property picker to the
     sibling Tee product's real variant availability matrix. Handles:
       1. Per-radio availability — disables Tee Size + Tee Colorway
          combinations that have no available Tee variant (e.g.
          Oxblood/XS and Forest/XS don't exist in Tee inventory).
       2. Auto-pivot — when the user picks a color that makes the
          currently-checked size invalid, the module re-selects the
          first available size for that color (without dispatching a
          synthetic change event — avoids recursion; the form
          serializer reads checked state at submit time).
       3. Display-only price swap — main price + CTA + "separately"
          value all swap on 2XL selection. Surcharge is derived from
          the Tee variants JSON (max 2XL price − min non-2XL price),
          so no hardcoded $5 differential.
       4. Colorway legend update — keeps "Tee colorway · {Color}"
          legend in sync with the checked color radio (the existing
          Module C `updateColorLabel` doesn't run on Complete because
          Complete's radios are emitted without `data-variant-option`).

     IMPORTANT — display-only at checkout:
     Complete remains a single Shopify variant ($82 / compareAt $93).
     Checkout will charge $82 regardless of which Tee Size line item
     property the customer selected. The 2XL `$87` price + `$98
     separately` shown on the PDP are operator-acknowledged display
     behavior. They become price-backed only when Complete gains real
     $87 variants in a future Shopify data ticket (see
     COMPLETE_VARIANTS_1_ARCHITECTURE_PLAN.md).

     Safety guarantees:
       - Targets only [data-picker-mode="tee-properties"]; no Tee or
         Print PDP can match (verified by data-picker-mode attribute
         only emitted by buy-panel.liquid when _format == 'complete'
         and Tee ref resolves).
       - Hidden form input[name="id"] is NEVER touched — the ATC
         always posts the real Complete variant ID.
       - All amounts derived from Liquid-rendered DOM text + Tee
         variants JSON; no hardcoded prices, no hardcoded variant IDs,
         no hardcoded option lists.
       - Auto-pivot sets `radio.checked = true` directly instead of
         dispatching a change event, so no recursion.
     ─────────────────────────────────────────────────────────────── */
  function initCompleteTeeSync() {
    var picker = document.querySelector('[data-variant-picker][data-picker-mode="tee-properties"]');
    if (!picker) return;
    var pdpBuy = picker.closest('.pdp-buy') || picker.closest('[data-pdp-buy]');
    if (!pdpBuy) return;

    // Tee variants JSON island (emitted by buy-panel.liquid only on
    // Complete PDP with a resolved Tee sibling). If missing, the
    // module falls back to no-availability-disabling + no-surcharge-
    // derivation behavior. Liquid guards against the missing case
    // already, so this is purely defensive.
    var teeJsonNode = pdpBuy.querySelector('[data-tee-variants-json]');
    var teeVariants = [];
    if (teeJsonNode) {
      try { teeVariants = JSON.parse((teeJsonNode.textContent || '').trim()); }
      catch (e) { teeVariants = []; }
      if (!Array.isArray(teeVariants)) teeVariants = [];
    }

    var priceCurrent = pdpBuy.querySelector('.buy-panel__price-current');
    var priceWas = pdpBuy.querySelector('.pdp-buy__price-was');
    var atcBtn = pdpBuy.querySelector('.buy-button[data-buy-button], button[name="add"]');
    var sizeFieldset = picker.querySelector('.pdp-buy__group--size');
    var colorFieldset = picker.querySelector('.pdp-buy__group--color');
    if (!priceCurrent || !atcBtn || !sizeFieldset) return;

    function parseDollarsToCents(text) {
      var m = (text || '').match(/\$?(\d+)(?:\.(\d{1,2}))?/);
      if (!m) return null;
      var dollars = parseInt(m[1], 10) || 0;
      var rem = m[2] ? parseInt(m[2].length === 1 ? m[2] + '0' : m[2], 10) : 0;
      return dollars * 100 + rem;
    }
    function centsToDisplay(cents) {
      var dollars = Math.floor(cents / 100);
      var rem = cents % 100;
      if (rem === 0) return '$' + dollars;
      return '$' + dollars + '.' + (rem < 10 ? '0' + rem : rem);
    }

    // Base values from initial Liquid render — authoritative.
    var basePriceText = priceCurrent.textContent.trim();
    var basePriceCents = parseDollarsToCents(basePriceText);
    if (basePriceCents == null) return;

    var baseAtcText = atcBtn.textContent.trim();
    var atcLabelPrefix = baseAtcText.replace(basePriceText, '').replace(/\s+$/, '');

    // "$93 separately" — parse base, strip the " separately" suffix
    // for re-emission. Falls back to null if priceWas is absent or
    // unparseable; module then skips the separately swap.
    var baseSeparatelyText = priceWas ? priceWas.textContent.trim() : null;
    var baseSeparatelyCents = baseSeparatelyText ? parseDollarsToCents(baseSeparatelyText) : null;
    var separatelySuffix = '';
    if (baseSeparatelyText) {
      // Capture the suffix after the price token (e.g. " separately")
      var sepMatch = baseSeparatelyText.match(/^\$?\d+(?:\.\d{1,2})?(.*)$/);
      separatelySuffix = sepMatch ? sepMatch[1] : '';
    }

    // Derive 2XL surcharge from Tee variants JSON.
    // surchargeCents = (max 2XL price) - (min non-2XL price). Both in
    // cents (Shopify variant.price is integer cents in JSON island).
    // If the Tee data has no 2XL or no XS–XL, surcharge is 0 (no
    // swap on 2XL selection — safe fallback).
    var teePrice2XLCents = null;
    var teePriceStdCents = null;
    for (var ti = 0; ti < teeVariants.length; ti++) {
      var tv = teeVariants[ti];
      if (!tv || !tv.options) continue;
      var isV2XL = false;
      for (var oi = 0; oi < tv.options.length; oi++) {
        if (String(tv.options[oi]).toUpperCase() === '2XL') { isV2XL = true; break; }
      }
      var p = typeof tv.price === 'number' ? tv.price : parseInt(tv.price, 10);
      if (isNaN(p)) continue;
      if (isV2XL) {
        if (teePrice2XLCents == null || p > teePrice2XLCents) teePrice2XLCents = p;
      } else {
        if (teePriceStdCents == null || p < teePriceStdCents) teePriceStdCents = p;
      }
    }
    var surchargeCents = (teePrice2XLCents != null && teePriceStdCents != null)
      ? Math.max(0, teePrice2XLCents - teePriceStdCents)
      : 0;

    // Identify the Tee size + color option indices within the option
    // array. Tee variants in JSON have v.options as an ordered array
    // matching the Shopify product option order. We need to map our
    // picker's Tee Size radio value + Tee Colorway radio value to a
    // Tee variant lookup.
    //
    // Tee product canonical option order: [Color, Size] (position 1
    // and 2). We detect this by scanning the variants' options for
    // values that look like sizes vs colors. Simpler + more robust:
    // try both orderings against the first variant's `title` (e.g.
    // "Black / XS") and the picker's known value sets. The findTeeVariant
    // function below handles this dynamically without assuming order.
    function isSizeValue(s) {
      var u = String(s || '').toUpperCase();
      return /^(XS|S|M|L|XL|2XL|3XL)$/.test(u);
    }
    function findTeeVariant(color, size) {
      for (var i = 0; i < teeVariants.length; i++) {
        var v = teeVariants[i];
        if (!v || !v.options) continue;
        var hasColor = false, hasSize = false;
        for (var j = 0; j < v.options.length; j++) {
          var optVal = String(v.options[j]);
          if (optVal === color) hasColor = true;
          if (optVal === size) hasSize = true;
        }
        if (hasColor && hasSize) return v;
      }
      return null;
    }

    function isVariantSelectable(v) {
      // Treat Shopify "available" as canonical. We do not check
      // inventory_quantity directly — Shopify already factors policy
      // (DENY / CONTINUE) + tracked state into `available`.
      return !!(v && v.available);
    }

    function syncAvailability() {
      var currentColorRadio = colorFieldset
        ? colorFieldset.querySelector('input[type="radio"]:checked')
        : null;
      var currentColor = currentColorRadio ? currentColorRadio.value : null;
      var currentSizeRadio = sizeFieldset.querySelector('input[type="radio"]:checked');
      var currentSize = currentSizeRadio ? currentSizeRadio.value : null;

      // Per-size availability: for each size radio, check if Tee has
      // an available variant matching (currentColor, thisSize).
      if (currentColor) {
        var sizeRadios = sizeFieldset.querySelectorAll('input[type="radio"]');
        for (var i = 0; i < sizeRadios.length; i++) {
          var sr = sizeRadios[i];
          var v = findTeeVariant(currentColor, sr.value);
          sr.disabled = !isVariantSelectable(v);
        }
      }

      // Per-color availability: for each color radio, check if Tee
      // has any available variant for that color (defensive — current
      // data has every color with ≥1 size, but a future inventory
      // outage could disable a colorway entirely).
      if (colorFieldset) {
        var colorRadios = colorFieldset.querySelectorAll('input[type="radio"]');
        for (var ci = 0; ci < colorRadios.length; ci++) {
          var cr = colorRadios[ci];
          var anyAvailable = false;
          for (var vi = 0; vi < teeVariants.length; vi++) {
            var tv = teeVariants[vi];
            if (!isVariantSelectable(tv) || !tv.options) continue;
            var match = false;
            for (var oj = 0; oj < tv.options.length; oj++) {
              if (String(tv.options[oj]) === cr.value) { match = true; break; }
            }
            if (match) { anyAvailable = true; break; }
          }
          cr.disabled = !anyAvailable;
        }
      }

      // Auto-pivot: if the currently checked size is now disabled,
      // re-select the first non-disabled size in the fieldset (in
      // visual order). Set `checked` directly without dispatching
      // change to avoid recursive sync. We update the legend / line
      // item properties / price below from the same code path.
      if (currentSizeRadio && currentSizeRadio.disabled) {
        var sizeRadios2 = sizeFieldset.querySelectorAll('input[type="radio"]');
        for (var k = 0; k < sizeRadios2.length; k++) {
          if (!sizeRadios2[k].disabled) {
            sizeRadios2[k].checked = true;
            currentSizeRadio.checked = false;
            break;
          }
        }
      }
    }

    function syncPriceDisplay() {
      var currentSizeRadio = sizeFieldset.querySelector('input[type="radio"]:checked');
      var is2XL = currentSizeRadio
        && String(currentSizeRadio.value).toUpperCase() === '2XL';

      var mainDisplay = is2XL && surchargeCents > 0
        ? centsToDisplay(basePriceCents + surchargeCents)
        : basePriceText;
      priceCurrent.textContent = mainDisplay;
      atcBtn.textContent = atcLabelPrefix + ' ' + mainDisplay;

      if (priceWas && baseSeparatelyCents != null) {
        var sepDisplay = is2XL && surchargeCents > 0
          ? centsToDisplay(baseSeparatelyCents + surchargeCents)
          : centsToDisplay(baseSeparatelyCents);
        priceWas.textContent = sepDisplay + separatelySuffix;
      }
    }

    function syncColorLegend() {
      if (!colorFieldset) return;
      var checkedColorRadio = colorFieldset.querySelector('input[type="radio"]:checked');
      if (!checkedColorRadio) return;
      var labelValue = colorFieldset.querySelector('.pdp-buy__label-value');
      var legend = colorFieldset.querySelector('legend.pdp-buy__label');
      if (!legend) return;
      if (labelValue) {
        labelValue.textContent = '· ' + checkedColorRadio.value;
      } else {
        var span = document.createElement('span');
        span.className = 'pdp-buy__label-value';
        span.textContent = ' · ' + checkedColorRadio.value;
        legend.appendChild(span);
      }
    }

    function sync() {
      syncAvailability();
      syncPriceDisplay();
      syncColorLegend();
    }

    picker.addEventListener('change', sync);
    sync();
  }

  /* ─── Module E · PDP buy-panel ATC AJAX intercept ──────────────
     PDP-COMPLETE-VARIANT-PICKER-MIGRATION FIX-1 · Issue 2 fix.

     The buy-panel `{% form 'product' %}` in snippets/buy-panel.liquid
     is NOT wrapped in Dawn's `<product-form>` custom element. The
     theme also does NOT load assets/product-form.js into any section
     that uses product-archive-main.liquid (Half Dome Tee, Print,
     Complete Edition all hit this section). Without an AJAX
     intercept, ATC posts a plain HTML form → server 302 → full-page
     navigation to /cart. Operator-observed regression after the
     Phase-4a Complete migration: clicking ATC took the user away
     from the PDP instead of opening the cart drawer.

     This module:
       1. Finds every <form action="/cart/add"> inside [data-pdp-buy]
          (the same scope that Module C's variant sync uses).
       2. Intercepts submit, preventDefault.
       3. POSTs to /cart/add.js with FormData including a sections
          render request for the cart-drawer section so we get the
          drawer HTML back in the same response.
       4. On success, publishes Dawn's PUB_SUB_EVENTS.cartUpdate event.
          The cart-drawer module (initCartDrawer) is already
          subscribed to that event and auto-opens the drawer when
          fired (see theme.js line 162).
       5. Swaps the drawer's inner HTML with the freshly-rendered
          section so the cart line shows the just-added variant.

     Safety / regression scope:
       - Targets only `[data-pdp-buy] form[action*="/cart/add"]`. Tee
         legacy PDP (he-pdp-main.liquid) uses its own form with its
         own inline AJAX (line 558 of that file) — different markup,
         not matched by this selector.
       - Variant id input is already kept in sync by Module C
         initVariantSync — this module just sends whatever the form
         currently holds.
       - Falls back silently if `window.publish` /
         `PUB_SUB_EVENTS.cartUpdate` aren't defined (pubsub.js failed
         to load) — drawer just won't auto-open in that case, but
         the cart add still succeeds.
       - On `data.status` error (e.g. variant unavailable, Shopify
         returns 422), surfaces the error to console and re-enables
         the button. No silent failure.
       - No hardcoded variant IDs, no product handle dependency, no
         price logic. The intercept is purely a network swap from
         multipart-POST-and-redirect → JSON-POST-and-section-swap.
     ─────────────────────────────────────────────────────────────── */
  function initBuyPanelAtcAjax() {
    /* PDP-RESKIN-ATC-DRAWER-HOTFIX-3 · auto-dismiss controller.
       ─────────────────────────────────────────────────────────────────
       After an ATC-triggered drawer.__open() the drawer auto-closes
       after 4000ms IF the shopper doesn't interact with it. Interaction-
       aware: any mouseenter / scroll / click / focusin inside the drawer
       cancels the timer PERMANENTLY (never re-armed) so an engaged user
       is never yanked out.

       Scope: module-level closure of initBuyPanelAtcAjax (this function).
       Variables persist across multiple form submits so a second ATC
       click while a timer is pending clears the old one and starts fresh.

       The cart-icon click path is NOT touched — it lives in initCartDrawer
       and never calls into this auto-dismiss code, so explicit
       cart-icon opens stay open until dismissed manually.

       Listeners are attached without `{once:true}` so the same handler
       can be removed cleanly by removeEventListener (the `fired` flag
       guards against double-fire). atcDismissCleanup removes all 4
       listeners; the timer callback also closes the drawer if still open.
       ───────────────────────────────────────────────────────────────── */
    var atcDismissTimer = null;
    var atcDismissCleanup = null;

    function clearAtcDismiss() {
      if (atcDismissTimer) {
        clearTimeout(atcDismissTimer);
        atcDismissTimer = null;
      }
      if (atcDismissCleanup) {
        try { atcDismissCleanup(); } catch (e) {}
        atcDismissCleanup = null;
      }
    }

    function startAtcDismiss(drawer) {
      clearAtcDismiss();
      if (!drawer) return;

      // PDP-RESKIN-ATC-DRAWER-HOTFIX-3-AMEND · grace window.
      // The drawer slides in from the right under a cursor that's resting
      // on the right-side ATC button. Without the grace window the
      // entrance fires mouseenter (and potentially focusin from a
      // programmatic focus call in open()) instantly, cancelling the
      // timer before the user engages. 400ms covers the slide animation
      // + focus shift + render settle; any deliberate decision-to-engage
      // takes longer than 400ms so the grace doesn't break the
      // intentional hover/scroll/click/tab tests.
      var armedAt = Date.now();
      var GRACE_MS = 400;

      var fired = false;
      function onInteract() {
        if (fired) return;
        if (Date.now() - armedAt < GRACE_MS) return;  // entrance noise — ignore
        fired = true;
        clearAtcDismiss();
      }

      // capture: true on scroll ensures we catch scroll events on any
      // descendant scrollable inside the drawer (scroll doesn't bubble
      // from the actual scrolling element in all browsers).
      // mousemove (added in HOTFIX-3-AMEND) covers the case where the
      // drawer slides in under a resting cursor: the entrance mouseenter
      // is inside the grace window and ignored, but any subsequent mouse
      // movement (= user actually engaged with drawer content) cancels
      // the timer. `fired` guards against the noisy stream of mousemove
      // events — only the first post-grace one is processed.
      drawer.addEventListener('mouseenter', onInteract);
      drawer.addEventListener('mousemove', onInteract);
      drawer.addEventListener('scroll', onInteract, true);
      drawer.addEventListener('click', onInteract);
      drawer.addEventListener('focusin', onInteract);

      atcDismissCleanup = function () {
        drawer.removeEventListener('mouseenter', onInteract);
        drawer.removeEventListener('mousemove', onInteract);
        drawer.removeEventListener('scroll', onInteract, true);
        drawer.removeEventListener('click', onInteract);
        drawer.removeEventListener('focusin', onInteract);
      };

      atcDismissTimer = setTimeout(function () {
        atcDismissTimer = null;
        if (atcDismissCleanup) {
          try { atcDismissCleanup(); } catch (e) {}
          atcDismissCleanup = null;
        }
        // Guard: close only if still open (user may have dismissed
        // manually via × / backdrop / ESC during the 4s window —
        // those paths don't fire mouseenter/click on the drawer
        // because the buttons are on the backdrop/document, not
        // inside the drawer element itself).
        if (typeof drawer.__isOpen === 'function' && drawer.__isOpen() && typeof drawer.__close === 'function') {
          try { drawer.__close(); }
          catch (e) { console.warn('[buy-panel ATC] auto-dismiss close failed:', e); }
        }
      }, 4000);
    }

    var forms = document.querySelectorAll('[data-pdp-buy] form[action*="/cart/add"]');
    if (!forms.length) return;

    forms.forEach(function (form) {
      // Guard: if a previous run already wired this form (e.g. Module E
      // runs twice due to dual init in dev), skip.
      if (form.dataset.atcAjaxWired === '1') return;
      form.dataset.atcAjaxWired = '1';

      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var hiddenId = form.querySelector('input[name="id"]');
        if (!hiddenId || !hiddenId.value) {
          console.warn('[buy-panel ATC] no variant id; aborting submit');
          return;
        }

        var atcBtn = form.querySelector('button[name="add"], button[type="submit"]');
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.setAttribute('aria-busy', 'true');
        }

        // TEE-PROPS-SYNC-SUBMIT · authoritative rewrite of the line-item
        // properties[_tee_color] and properties[_tee_size] hidden inputs at
        // submit time, just before FormData captures values. Belt-and-
        // suspenders with the sync() rewrite — covers the case where the
        // change-event sync didn't run (e.g. URL-bound default variant
        // never fired a change), and the case where the JSON island /
        // .pdp-buy ancestor lookup in initVariantSync resolved a different
        // wrapper. Reads the selected variant directly from the JSON
        // island in the current form's .pdp-buy. No-op on PDPs that don't
        // emit those hidden inputs (Print, Premiere, Master, etc.).
        try {
          var teeColorInput = form.querySelector('input[name="properties[_tee_color]"]');
          var teeSizeInput  = form.querySelector('input[name="properties[_tee_size]"]');
          if (teeColorInput || teeSizeInput) {
            var pdpBuyEl = form.closest('.pdp-buy') || form.closest('[data-pdp-buy]');
            var jsonEl = pdpBuyEl && pdpBuyEl.querySelector('[data-product-variants-json]');
            if (jsonEl) {
              var variantsArr = JSON.parse((jsonEl.textContent || '').trim());
              var selectedVariant = null;
              for (var vi = 0; vi < variantsArr.length; vi++) {
                if (String(variantsArr[vi].id) === String(hiddenId.value)) {
                  selectedVariant = variantsArr[vi]; break;
                }
              }
              if (selectedVariant) {
                if (teeColorInput && selectedVariant.option1 != null) {
                  teeColorInput.value = selectedVariant.option1;
                }
                if (teeSizeInput && selectedVariant.option2 != null) {
                  teeSizeInput.value = selectedVariant.option2;
                }
              }
            }
          }
        } catch (eTeeProps) {
          console.warn('[buy-panel ATC] tee-props submit-time sync failed:', eTeeProps);
        }

        var fd = new FormData(form);

        // PDP-CART-DRAWER-RENDERING-ENDPOINT-FIX-2 · split pattern:
        //   STEP 1 · POST /cart/add.js (mutation only, no sections field —
        //            the `/cart.js?sections=` family is non-functional on
        //            this theme)
        //   STEP 2 · GET /?section_id=<runtime_id> (text/html response)
        //            parse + swap drawer.innerHTML + rewire close + propagate count
        // The runtime instance id from `data-section-id` is the correct
        // addressable id for the `/?section_id=` endpoint.
        var drawer = document.querySelector('[data-cart-drawer], #CartDrawer');
        var sectionId = drawer && drawer.getAttribute('data-section-id');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: fd
        })
          .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); })
          .then(function (res) {
            if (!res.ok || res.data.status === 422) {
              // Error path · Shopify returns { status: 422, description: "...", message: "..." }
              console.error('[buy-panel ATC]', res.data.description || res.data.message || res);
              return null;
            }
            return res;
          })
          .then(function (res) {
            if (!res) return null;
            /* addendum 6 §2 · add_to_cart in the HE namespace.
               Shopify's own cart event carries none of getCommonProps(), so it
               cannot be joined to any interaction event by terrain, world or
               format. This one goes through HE.track, so every interaction
               event on the page can be tied to the outcome. Fired here, on a
               confirmed 200 from /cart/add.js — never on the click, which
               would count failed adds as sales. `edition` uses the same
               tee|print|complete vocabulary as select_format so the two join. */
            try {
              if (window.HE && typeof window.HE.track === 'function' && res.data) {
                var line = res.data;
                var opts = line.options_with_values || [];
                var pick = function (needle) {
                  for (var i = 0; i < opts.length; i++) {
                    if ((opts[i].name || '').toLowerCase().indexOf(needle) >= 0) return opts[i].value;
                  }
                  return '';
                };
                var props = line.properties || {};
                var panelEl = document.querySelector('[data-buy-panel]');
                // A print has one variant and no options, so there is no size
                // option to read. Its size is a product metafield
                // (horizon.format_print_size), carried on the he-page-meta
                // island. `color` stays empty on a print — there is no
                // colourway, and an empty field is the honest answer.
                var pageMeta = (window.HE && typeof window.HE.getPageMeta === 'function')
                  ? window.HE.getPageMeta() : {};
                window.HE.track('add_to_cart', {
                  variant_id: line.variant_id || line.id || '',
                  size:  pick('size')  || props._tee_size  || pageMeta.print_size || '',
                  color: pick('color') || props._tee_color || '',
                  price: line.price != null ? (line.price / 100) : undefined,
                  edition: (panelEl && panelEl.getAttribute('data-format')) ||
                           props._product_format || '',
                  via: heAtcVia,
                  viewport: (window.matchMedia && window.matchMedia('(max-width: 991px)').matches)
                    ? 'mobile' : 'desktop'
                });
              }
            } catch (eTrack) {
              console.warn('[buy-panel ATC] add_to_cart track failed:', eTrack);
            }
            // STEP 2 · fetch fresh drawer HTML via the classic ?section_id= endpoint.
            if (!drawer || !sectionId) return res;
            return fetch('/?section_id=' + encodeURIComponent(sectionId), {
              credentials: 'same-origin',
              headers: { 'Accept': 'text/html' }
            })
              .then(function (sr) {
                if (!sr.ok) {
                  console.warn('[buy-panel ATC] GET /?section_id=', sr.status);
                  return res;
                }
                return sr.text().then(function (html) {
                  try {
                    var parser = new DOMParser();
                    var doc = parser.parseFromString(html, 'text/html');
                    var freshDrawer = doc.querySelector('[data-cart-drawer], #CartDrawer');
                    if (freshDrawer) {
                      drawer.innerHTML = freshDrawer.innerHTML;

                      // PDP-CART-DRAWER-ATC-QA-FIX-1 · re-bind close
                      // button (lives INSIDE drawer; bound directly
                      // not via delegation; dies on innerHTML swap).
                      if (typeof drawer.__rewireCloseButtons === 'function') drawer.__rewireCloseButtons();
                      // FIX-CART-01 · gift toggle/message/email listeners
                      // also die on the innerHTML swap. Re-init reattaches
                      // them to the new [data-cart-gift] nodes.
                      initGiftPanel();

                      // PDP-CART-DRAWER-ATC-QA-FIX-1 · propagate
                      // updated count to header chip(s) that live
                      // OUTSIDE the drawer.
                      var newCountEl = drawer.querySelector('[data-cart-count]');
                      if (newCountEl) {
                        var newCount = newCountEl.textContent;
                        document.querySelectorAll('[data-cart-count]').forEach(function (c) {
                          if (!drawer.contains(c)) c.textContent = newCount;
                        });
                      }

                      // PDP-RESKIN-ATC-DRAWER-NO-OPEN hotfix · open the
                      // drawer directly after the section-render swap
                      // completes. The legacy pubsub `cartUpdate` publish
                      // below is a no-op (window.PUB_SUB_EVENTS is
                      // undefined — `const` from constants.js doesn't
                      // attach to window), so the cart-drawer's own
                      // subscriber never fires. Calling drawer.__open()
                      // reuses the SAME open function the cart-icon
                      // trigger uses — no new open path introduced.
                      // PDP-MOBILE-4 · on mobile the confirmation sheet is the
                      // feedback, so the drawer does not auto-open — one add,
                      // one piece of feedback. handleAdded returns false on
                      // desktop (and if the sheets are absent), leaving the
                      // drawer path exactly as it was.
                      var _mobileHandled = false;
                      try {
                        _mobileHandled = !!(window.HEPdpMobile &&
                          typeof window.HEPdpMobile.handleAdded === 'function' &&
                          window.HEPdpMobile.handleAdded(null));
                      } catch (eSheet) {
                        console.warn('[buy-panel ATC] mobile sheet failed:', eSheet);
                      }
                      if (!_mobileHandled && typeof drawer.__open === 'function') {
                        try { drawer.__open(); }
                        catch (eOpen) { console.warn('[buy-panel ATC] drawer.__open failed:', eOpen); }
                        // PDP-RESKIN-ATC-DRAWER-HOTFIX-3 · arm the auto-
                        // dismiss timer (4000ms · cancels on first user
                        // interaction · never re-arms once cancelled).
                        startAtcDismiss(drawer);
                      }
                    }
                  } catch (e) {
                    console.warn('[buy-panel ATC] drawer parse:', e);
                  }
                  return res;
                });
              });
          })
          .then(function (res) {
            if (!res) return;
            // Publish cartUpdate so initCartDrawer's subscriber auto-opens
            // the drawer (note: currently silently no-ops because
            // `window.PUB_SUB_EVENTS` is undefined — `const` from
            // constants.js doesn't attach to window; tracked as separate
            // follow-up PDP-CART-PUBSUB-FIX).
            if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'buy-panel-atc',
                productVariantId: hiddenId.value,
                cartData: res.data
              });
            }
          })
          .catch(function (err) {
            console.error('[buy-panel ATC] network error:', err);
          })
          .finally(function () {
            if (atcBtn) {
              atcBtn.disabled = false;
              atcBtn.removeAttribute('aria-busy');
            }
          });
      });
    });
  }

  /* ─── Module G · Cart drawer Gift mode (PDP-CART-DRAWER-GIFT-MODE-1)
     PHASE-2G — ORDER-LEVEL GIFT (per-line model torn out).

     An order is either a self-purchase or a gift. Gift state lives in
     cart attributes:
       · cart.attributes.gift          ("true" / "false")
       · cart.attributes.gift_message  (free text, up to 200 chars)

     The Liquid renders the section's initial state from those attributes
     on every drawer render. JS wires:
       · Toggle (.cart-gift__checkbox) → POST /cart/update.js with
         {attributes:{gift:"true"|"false"}}; reveal/hide the message
         body via the .cart-gift--active class. No section re-render.
       · Message textarea → debounced (~500ms) POST /cart/update.js with
         {attributes:{gift_message:"..."}}. No re-render — focus preserved.

     Legacy attributes _gift_enabled and _hide_prices (Phase 1 cart-level
     gift) are cleared piggyback on every toggle write — harmless if
     already empty; clears stale residue from old carts.

     No line-item writes happen here. Quantities, _archive_id, and
     _product_format stay untouched.
     ─────────────────────────────────────────────────────────────── */
  function initGiftPanel() {
    var drawer = document.querySelector('[data-cart-drawer], #CartDrawer');
    if (!drawer) return;
    var section = drawer.querySelector('[data-cart-gift]');
    if (!section) return;

    var toggle  = section.querySelector('[data-cart-gift-toggle]');
    var msgEl   = section.querySelector('[data-cart-gift-message]');
    var counter = section.querySelector('[data-cart-gift-counter]');
    var emailEl = section.querySelector('[data-cart-gift-email]');
    var DEBOUNCE_MS = 500;
    var debounceHandle = null;
    var inFlight = false;

    function setActive(active) {
      section.classList.toggle('cart-gift--active', !!active);
    }

    function updateCounter() {
      if (!counter || !msgEl) return;
      var len = (msgEl.value || '').length;
      counter.textContent = len + ' / 200';
      if (len >= 180) counter.classList.add('cart-gift__counter--near-limit');
      else counter.classList.remove('cart-gift__counter--near-limit');
    }

    // addendum 1 §2 · a save that arrived while another was in flight used to
    // be dropped (`return Promise.resolve(null)`), so typing a message and then
    // an email lost the email. Saves are now merged into a pending object and
    // sent when the current request finishes — last value per key wins, and
    // nothing is discarded.
    var pendingAttrs = null;

    function flushPending() {
      if (!pendingAttrs || inFlight) return;
      var next = pendingAttrs;
      pendingAttrs = null;
      postAttributes(next);
    }

    function postAttributes(attrs) {
      if (inFlight) {
        pendingAttrs = Object.assign({}, pendingAttrs || {}, attrs || {});
        return Promise.resolve(null);
      }
      inFlight = true;
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) {
        if (!r.ok) { console.warn('[gift] /cart/update.js', r.status); inFlight = false; flushPending(); return null; }
        return r.json();
      }).then(function (cart) {
        inFlight = false;
        flushPending();
        return cart;
      }).catch(function (e) {
        console.warn('[gift] /cart/update.js error', e);
        inFlight = false;
        flushPending();
        return null;
      });
    }

    if (toggle) {
      toggle.addEventListener('change', function () {
        var active = !!toggle.checked;
        setActive(active);
        var attrs = {
          gift: active ? 'true' : 'false',
          _gift_enabled: '',
          _hide_prices: ''
        };
        if (!active && emailEl) {
          emailEl.value = '';
          attrs.gift_recipient_email = '';
        }
        postAttributes(attrs);
        if (active && msgEl) {
          try { msgEl.focus(); } catch (e) {}
        }
      });
    }

    if (msgEl) {
      msgEl.addEventListener('input', function () {
        updateCounter();
        if (debounceHandle) clearTimeout(debounceHandle);
        debounceHandle = setTimeout(function () {
          debounceHandle = null;
          postAttributes({ gift_message: msgEl.value || '' });
        }, DEBOUNCE_MS);
      });
      msgEl.addEventListener('blur', function () {
        if (debounceHandle) { clearTimeout(debounceHandle); debounceHandle = null; }
        postAttributes({ gift_message: msgEl.value || '' });
      });
    }

    // addendum 2 §5 · J typed "/chart" into the recipient field. An invalid
    // address is not saved and says so inline — but it never blocks checkout:
    // the gift is the order, the email is a courtesy the studio sends by hand.
    var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (emailEl) {
      var emailErr = section.querySelector('[data-cart-gift-email-error]');
      if (!emailErr) {
        emailErr = document.createElement('p');
        // not given `.mono`: the drawer's inline <style> forces .mono to 12px
        // uppercase, which is wrong for a sentence the buyer has to read —
        // the same reason .cart-gift__help avoids it.
        emailErr.className = 'cart-gift__error';
        emailErr.setAttribute('data-cart-gift-email-error', '');
        emailErr.setAttribute('role', 'status');
        emailErr.hidden = true;
        emailEl.parentNode.insertBefore(emailErr, emailEl.nextSibling);
      }
      emailEl.addEventListener('blur', function () {
        var val = (emailEl.value || '').trim();
        if (val && !EMAIL_RE.test(val)) {
          emailErr.textContent = 'Enter a valid email or leave it blank';
          emailErr.hidden = false;
          emailEl.setAttribute('aria-invalid', 'true');
          return;                                  // not saved
        }
        emailErr.hidden = true;
        emailEl.removeAttribute('aria-invalid');
        postAttributes({ gift_recipient_email: val });
      });
      emailEl.addEventListener('input', function () {
        if (!emailErr.hidden) { emailErr.hidden = true; emailEl.removeAttribute('aria-invalid'); }
      });
    }

    // addendum 1 §2 · on submit, cancel any debounce still waiting and post the
    // current values immediately. The form now carries the fields, so this is
    // belt and braces — it also stops a stale POST racing the navigation.
    var giftForm = document.getElementById('CartDrawerForm');
    if (giftForm) {
      giftForm.addEventListener('submit', function () {
        if (debounceHandle) { clearTimeout(debounceHandle); debounceHandle = null; }
        var attrs = {};
        if (msgEl) attrs.gift_message = msgEl.value || '';
        if (emailEl) {
          var emailVal = (emailEl.value || '').trim();
          // addendum 3 · the blur guard does not fire when the buyer taps
          // checkout straight from the field, and the input is inside this
          // form — so "/chart" would ride the POST onto the order and the
          // studio would send the manual gift email into nowhere. Blank the
          // field itself, not just the ajax attrs: the form POST is what
          // actually reaches the cart, and an empty value clears any address
          // saved earlier. Checkout is never blocked.
          if (emailVal && !EMAIL_RE.test(emailVal)) {
            emailEl.value = '';
            emailVal = '';
            if (emailErr) {
              emailErr.textContent = 'Enter a valid email or leave it blank';
              emailErr.hidden = false;
            }
            emailEl.setAttribute('aria-invalid', 'true');
          }
          attrs.gift_recipient_email = emailVal;
        }
        postAttributes(attrs);
      });
    }

    updateCounter();
    try { console.log('[gift] order-level initialized'); } catch (e) {}
  }

  /* ─── Module F · Cart auto-swap to Complete Edition ──────────────
     PDP-CART-DRAWER-CANONICAL-ALIGN-1 · Phase 2 of 4
     Canonical reference: 20_CART_BEHAVIOR_SPEC.md §6

     When the cart contains a Tee + Print of the same terrain (same
     `properties._archive_id`) and NO existing Complete for that
     terrain, swap Tee + Print pairs for matching Complete Edition
     variants atomically.

     Operator decisions (PDP_CART_DRAWER_CANONICAL_ALIGN_1_DIRECTIVE
     2026-05-20):
       · Decision 1: Automatic Discounts ("Archive Set S/L") stay
         ACTIVE in this ticket. They are the fallback path for the 7
         terrains that don't yet have Complete Edition products.
         When auto-swap fires (Half Dome) the Tee+Print lines are
         removed BEFORE the discount evaluates → no discount applied
         → Complete @ $82. When auto-swap silent-skips (Crater Lake
         etc.) the discount applies → $93 − $11 = $82.
       · Decision 2: SILENT SKIP if the Complete PRODUCT doesn't
         exist for the archive_id (entire `{tee_handle}-complete-
         edition` 404s). ERROR TOAST if Complete product exists but
         the matching color/size variant is unavailable.

     Loop prevention: Module F only reacts to cartUpdate events whose
     `source === 'buy-panel-atc'`. Its own /cart/change.js and
     /cart/add.js calls don't publish cartUpdate (the publisher is
     Module E in buy-panel-ATC, and initCartLine in cart-drawer-line
     — Module F doesn't republish), so there's no recursion risk.
     Per-archive_id 500 ms debounce guards against rapid double-fires.
     A global `inSwap` flag suppresses further swap attempts while
     mutations are in flight.

     Complete handle derivation by convention:
       `{tee_handle}-complete-edition`
     For Half Dome: `half-dome` → `half-dome-complete-edition` ✓
     For terrains without a Complete product yet (Crater Lake etc.),
     `/products/{handle}.js` returns 404 → silent skip.
     ─────────────────────────────────────────────────────────────── */
  function initCartAutoSwap() {
    // PDP-CART-DRAWER-UPSELL-PIVOT-1 · Module F PAUSED.
    // The auto-swap path has been replaced by a passive Complete Edition
    // upsell card (snippets/cart-bundle-resolver.liquid +
    // snippets/cart-complete-upsell.liquid) plus the rebranded
    // "Complete Edition Savings" Automatic Discount that delivers the $11
    // saving natively when both Tee + Print are in cart.
    //
    // Code preserved below for potential post-launch reactivation. The
    // `if (false)` guard prevents execution — including the prior
    // `[auto-swap] pubsub not available` console.warn, which was the
    // first symptom reported in PDP_CART_DRAWER_AUTOSWAP_REGRESSION
    // testing (`window.PUB_SUB_EVENTS` is undefined because constants.js
    // declares PUB_SUB_EVENTS as a `const`, not a window property).
    if (false) {
    var DEBOUNCE_MS = 500;
    var lastSwapAt = Object.create(null);
    var inSwap = false;

    if (!window.subscribe || !window.PUB_SUB_EVENTS || !window.PUB_SUB_EVENTS.cartUpdate) {
      return;
    }

    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
      if (!payload || payload.source !== 'buy-panel-atc') return;
      if (inSwap) return;
      detectAndSwap();
    });

    function detectAndSwap() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var groups = Object.create(null);
          (cart.items || []).forEach(function (item) {
            var p = item.properties || {};
            var aid = p._archive_id;
            var fmt = p._product_format;
            if (!aid || !fmt) return;
            if (!groups[aid]) groups[aid] = { tee: [], print: [], complete: [] };
            if (groups[aid][fmt]) groups[aid][fmt].push(item);
          });

          Object.keys(groups).forEach(function (aid) {
            var g = groups[aid];
            if (g.complete.length > 0) return;
            if (g.tee.length === 0 || g.print.length === 0) return;
            var now = Date.now();
            if (lastSwapAt[aid] && (now - lastSwapAt[aid]) < DEBOUNCE_MS) return;
            lastSwapAt[aid] = now;
            attemptSwap(aid, g);
          });
        })
        .catch(function (err) {
          console.warn('[auto-swap] /cart.js fetch failed:', err);
        });
    }

    function attemptSwap(archiveId, group) {
      var teeLine = group.tee[0];
      var teeProps = teeLine.properties || {};
      var teeColor = teeProps._tee_color;
      var teeSize = teeProps._tee_size;
      if (!teeColor || !teeSize) {
        console.warn('[auto-swap] Tee line missing _tee_color/_tee_size; cannot resolve Complete variant');
        return;
      }

      var teeHandle = teeLine.handle;
      var completeHandle = teeHandle + '-complete-edition';

      // STEP 1 · does the Complete product exist?
      fetch('/products/' + encodeURIComponent(completeHandle) + '.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) {
          // 404 → silent skip per Decision 2 case A.
          // Discount fallback (Archive Set S/L) will deliver the saving.
          if (!r.ok) return null;
          return r.json();
        })
        .then(function (completeProduct) {
          if (!completeProduct) return;

          // STEP 2 · find the matching variant within the Complete product.
          var match = (completeProduct.variants || []).find(function (v) {
            return v.option1 === teeColor && v.option2 === teeSize && v.available;
          });
          if (!match) {
            // Decision 2 case B: error toast.
            if (typeof window.showToast === 'function') {
              window.showToast('error',
                "Couldn't combine — Complete Edition not available in that color/size.");
            }
            return;
          }

          executeSwap(archiveId, group, match);
        })
        .catch(function (err) {
          // Network error · treat as silent skip; don't surface to user.
          console.warn('[auto-swap] Complete product lookup failed:', err);
        });
    }

    // PRICE-TIER-FIX-4 · the swap toast used to claim a flat "save $11" per
    // pair no matter which variant was combined. At the upper size tiers the
    // Complete variant's compare_at_price equals its price, so there is no
    // saving to claim. Compute it from the variant actually being added
    // (/products/<handle>.js returns price + compare_at_price in cents) and
    // drop the clause when the real figure is zero.
    function swapSavingCents(completeVariant, pairs) {
      if (!completeVariant) return 0;
      var compare = completeVariant.compare_at_price;
      var price = completeVariant.price;
      if (!compare || !(compare > price)) return 0;
      return (compare - price) * (pairs || 0);
    }

    function swapToastTitle(completeVariant, pairs) {
      var saving = swapSavingCents(completeVariant, pairs);
      if (saving <= 0) return 'Combined into Complete Edition';
      var dollars = Math.floor(saving / 100);
      var remainder = saving % 100;
      var amount = remainder === 0
        ? '$' + dollars
        : '$' + dollars + '.' + (remainder < 10 ? '0' + remainder : '' + remainder);
      return 'Combined into Complete Edition · save ' + amount;
    }

    function executeSwap(archiveId, group, completeVariant) {
      var teeQty = group.tee.reduce(function (s, i) { return s + (i.quantity || 0); }, 0);
      var printQty = group.print.reduce(function (s, i) { return s + (i.quantity || 0); }, 0);
      var pairs = Math.min(teeQty, printQty);
      if (pairs <= 0) return;

      inSwap = true;

      var teeLine = group.tee[0];
      var printLine = group.print[0];
      var newTeeQty = (teeLine.quantity || 0) - pairs;
      var newPrintQty = (printLine.quantity || 0) - pairs;

      // Capture undo state BEFORE mutating.
      var undoState = {
        teeVariantId: teeLine.variant_id,
        teePairsRemoved: pairs,
        teeProps: Object.assign({}, teeLine.properties || {}),
        printVariantId: printLine.variant_id,
        printPairsRemoved: pairs,
        printProps: Object.assign({}, printLine.properties || {}),
        completeVariantId: completeVariant.id,
        completeQty: pairs,
        completeLineKey: null,
        archiveId: archiveId
      };

      changeQty(teeLine.key, newTeeQty)
        .then(function () { return changeQty(printLine.key, newPrintQty); })
        .then(function () {
          return addLine(completeVariant.id, pairs, {
            _archive_id: archiveId,
            _product_format: 'complete',
            _tee_color: completeVariant.option1,
            _tee_size: completeVariant.option2
          });
        })
        .then(function () { return renderDrawer(); })
        .then(function (postCart) {
          var added = (postCart.items || []).find(function (i) {
            return i.variant_id === completeVariant.id;
          });
          if (added) undoState.completeLineKey = added.key;

          if (typeof window.showToast === 'function') {
            window.showToast('success', {
              title: swapToastTitle(completeVariant, pairs),
              detail: 'Tee + Print → Complete Edition',
              action: { label: 'Undo', handler: function () { undoSwap(undoState); } }
            });
          }
        })
        .catch(function (err) {
          console.error('[auto-swap] swap failed:', err);
          if (typeof window.showToast === 'function') {
            window.showToast('error', "Couldn't combine — cart unchanged.");
          }
        })
        .then(null, function () {})
        .then(function () { inSwap = false; });
    }

    function undoSwap(undoState) {
      if (inSwap) return;
      inSwap = true;

      var ops = [];
      if (undoState.completeLineKey) {
        ops.push(changeQty(undoState.completeLineKey, 0));
      }
      ops.push(addLine(undoState.teeVariantId, undoState.teePairsRemoved, undoState.teeProps));
      ops.push(addLine(undoState.printVariantId, undoState.printPairsRemoved, undoState.printProps));

      ops.reduce(function (chain, op) {
        return chain.then(function () { return op; });
      }, Promise.resolve())
        .then(function () { return renderDrawer(); })
        .then(function () {
          if (typeof window.showToast === 'function') {
            window.showToast('info', 'Combine undone · Tee + Print restored.');
          }
        })
        .catch(function (err) {
          console.error('[auto-swap] undo failed:', err);
          if (typeof window.showToast === 'function') {
            window.showToast('error', "Couldn't undo — cart in mixed state.");
          }
        })
        .then(null, function () {})
        .then(function () { inSwap = false; });
    }

    function changeQty(lineKey, newQty) {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: newQty })
      });
    }

    function addLine(variantId, qty, properties) {
      var body = { id: variantId, quantity: qty };
      if (properties && Object.keys(properties).length > 0) body.properties = properties;
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
    }

    function renderDrawer() {
      var drawer = document.querySelector('[data-cart-drawer], #CartDrawer');
      if (!drawer) return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      // PDP-CART-DRAWER-RENDERING-ENDPOINT-FIX-2 · GET /?section_id=<runtime_id>
      // returns text/html (not JSON). Module F is paused (if(false) wrap),
      // but keep this consistent for any future reactivation.
      var sectionId = drawer.getAttribute('data-section-id') || '';
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!sectionId) return cart;
          return fetch('/?section_id=' + encodeURIComponent(sectionId), {
            credentials: 'same-origin',
            headers: { 'Accept': 'text/html' }
          })
            .then(function (sr) { return sr.ok ? sr.text() : null; })
            .then(function (html) {
              if (!html) return cart;
              try {
                var parser = new DOMParser();
                var doc = parser.parseFromString(html, 'text/html');
                var fresh = doc.querySelector('[data-cart-drawer], #CartDrawer');
                if (fresh) {
                  drawer.innerHTML = fresh.innerHTML;
                  if (typeof drawer.__rewireCloseButtons === 'function') drawer.__rewireCloseButtons();
                  // FIX-CART-01 · gift listeners die on innerHTML swap;
                  // re-init reattaches them. Module F is currently paused
                  // (see `if (false)` guard below) so this is dead code
                  // for now — kept consistent with the other two swap
                  // sites so reactivation doesn't reintroduce the bug.
                  initGiftPanel();
                  var newCountEl = drawer.querySelector('[data-cart-count]');
                  if (newCountEl) {
                    var newCount = newCountEl.textContent;
                    document.querySelectorAll('[data-cart-count]').forEach(function (c) {
                      if (!drawer.contains(c)) c.textContent = newCount;
                    });
                  }
                }
              } catch (e) {
                console.warn('[auto-swap] drawer swap failed:', e);
              }
              return cart;
            });
        });
    }
    } // end if (false) — Module F paused per PDP-CART-DRAWER-UPSELL-PIVOT-1
  }

  /* ─── Module G · PDP Re-skin variant bridge (PDP-RESKIN-STAGE-4) ─────
     Forwards click events on the new visible .swatch / .size buttons
     to the hidden radio inputs (the existing variant picker backing
     store). The radios fire native 'change' events which Module C
     (initVariantSync) listens to and updates the hidden name="id"
     input + repaints price / ATC label / compare-at line.

     This module does NOT rebuild the variant picker — it's a thin
     click-translation layer. New buttons emit:
       data-pdp-swatch  + data-option-name + data-option-value
       data-pdp-size    + data-option-name + data-option-value

     Both data-option-name use the EXACT Shopify option name (e.g.
     "Color" or "Tee Color"), which matches the radio's
     name="options[<exact_name>]" attribute. The forwarding
     querySelector finds the matching radio by name + value.

     aria-pressed sync on sibling buttons (visual highlight) happens
     immediately on click; the radio change → Module C update is
     async-ish but fast (next tick).

     Scope: only fires on PDPs that emit the new .swatch / .size
     buttons (Tee + Complete archive products). Print PDP has no
     swatches and no size buttons (single 18×24 button is a passive
     display only, no data-pdp-* attr).
     ─────────────────────────────────────────────────────────────── */
  function initPdpReskinVariantBridge() {
    var pdpBuy = document.querySelector('[data-pdp-buy]');
    if (!pdpBuy) return;
    var picker = pdpBuy.querySelector('[data-variant-picker]');
    if (!picker) return;

    function forwardToRadio(optionName, optionValue) {
      if (!optionName) return false;
      var radio = null;
      try {
        var selector = 'input[type="radio"][name="options[' + optionName.replace(/"/g, '\\"') + ']"][value="' + optionValue.replace(/"/g, '\\"') + '"]';
        radio = picker.querySelector(selector);
      } catch (eSel) {
        // Malformed selector (unusual chars in option name/value) — fall through
        // to the iteration fallback below. A prior bug here had a bad selector
        // string that threw SyntaxError and silently killed click forwarding.
        radio = null;
      }
      if (!radio) {
        // Iteration fallback: name/value comparison via DOM properties — no
        // CSS escaping concerns, handles any string content.
        var radios = picker.querySelectorAll('input[type="radio"][data-variant-option]');
        for (var i = 0; i < radios.length; i++) {
          if (radios[i].name === 'options[' + optionName + ']' && radios[i].value === optionValue) {
            radio = radios[i]; break;
          }
        }
      }
      if (!radio) {
        console.warn('[pdp-reskin] no radio match for', optionName, '=', optionValue);
        return false;
      }
      if (radio.checked) return true;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    function syncPressed(selectorAttr, optionName, optionValue) {
      var btns = pdpBuy.querySelectorAll('[' + selectorAttr + ']');
      btns.forEach(function (b) {
        if (b.getAttribute('data-option-name') !== optionName) return;
        b.setAttribute('aria-pressed', b.getAttribute('data-option-value') === optionValue ? 'true' : 'false');
      });
    }

    pdpBuy.addEventListener('click', function (e) {
      var swatch = e.target.closest && e.target.closest('[data-pdp-swatch]');
      if (swatch) {
        e.preventDefault();
        var sn = swatch.getAttribute('data-option-name');
        var sv = swatch.getAttribute('data-option-value');
        if (forwardToRadio(sn, sv)) {
          syncPressed('data-pdp-swatch', sn, sv);
          var label = pdpBuy.querySelector('[data-current-color-name]');
          if (label && sn && sn.toLowerCase().indexOf('color') >= 0) label.textContent = sv;
        }
        return;
      }
      var size = e.target.closest && e.target.closest('[data-pdp-size]');
      if (size) {
        e.preventDefault();
        var zn = size.getAttribute('data-option-name');
        var zv = size.getAttribute('data-option-value');
        if (forwardToRadio(zn, zv)) {
          syncPressed('data-pdp-size', zn, zv);
        }
        return;
      }
    });

    try { console.log('[pdp-reskin] variant bridge initialized'); } catch (e) {}
  }

  /* ─── Module H · PDP mobile (PDP-MOBILE · 2026-09-18) ──────────────
     Mobile-only product-page behaviour, all of it gated on
     max-width: 991px so desktop is untouched:

       §3  the sticky bar is revealed by an IntersectionObserver on the
           real .atc (threshold 0.35). Not visible → bar shown. It shows
           from first paint because the real button starts below the fold.
       §4  the ATC is never disabled. With no size chosen it opens a size
           bottom sheet; choosing a size there adds to cart and opens a
           confirmation sheet. Both sheets drag down or tap-backdrop to
           dismiss.
       §4  on mobile the confirmation sheet replaces the cart drawer's
           auto-open, so one add produces one piece of feedback. The
           drawer itself is untouched and still opens from the cart icon.

     Everything reads the variant JSON the panel already emits — no size,
     price or availability list is duplicated here.
     ────────────────────────────────────────────────────────────────── */
  function initPdpMobile() {
    var MQ = '(max-width: 991px)';
    function isMobile() {
      return window.matchMedia && window.matchMedia(MQ).matches;
    }

    var pdpBuy = document.querySelector('[data-pdp-buy] .pdp-buy[data-buy-panel]') ||
                 document.querySelector('[data-pdp-buy]');
    var jsonNode = document.querySelector('[data-product-variants-json]');
    var variants = [];
    if (jsonNode) {
      try { variants = JSON.parse((jsonNode.textContent || '').trim()) || []; }
      catch (e) { variants = []; }
    }

    function money(cents) {
      if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
      var d = Math.floor(cents / 100), r = cents % 100;
      if (r === 0) return '$' + d;
      return '$' + d + '.' + (r < 10 ? '0' + r : '' + r);
    }

    // Cheapest available variant matching whatever is already selected.
    // Used for the button label before a size is chosen.
    function cheapestVariant(list, selected) {
      var pool = (list && list.length ? list : variants) || [];
      var best = null;
      for (var i = 0; i < pool.length; i++) {
        var v = pool[i];
        if (!v || !v.available) continue;
        if (selected && selected.length && v.options && v.options[0] &&
            selected[0] && v.options[0] !== selected[0]) continue;
        if (!best || v.price < best.price) best = v;
      }
      return best;
    }

    // Exposed so initVariantSync (Module C) can reuse the same helpers
    // without duplicating the mobile test or the price maths.
    window.HEPdpMobile = { isMobile: isMobile, money: money, cheapestVariant: cheapestVariant };

    if (!pdpBuy) return;

    var atcBtn = document.querySelector('[data-pdp-buy] [data-pdp-atc]');
    var stickyBar = document.querySelector('[data-sticky-atc]');
    var stickyBtn = stickyBar ? stickyBar.querySelector('[data-sticky-atc-btn]') : null;
    var backdrop = document.querySelector('[data-pdp-sheet-backdrop]');
    var sheetSize = document.querySelector('[data-pdp-sheet="size"]');
    var sheetAdded = document.querySelector('[data-pdp-sheet="added"]');
    var form = document.querySelector('[data-pdp-buy] form[action*="/cart/add"]');

    /* ── §3 · sticky reveal ─────────────────────────────────────────── */
    if (stickyBar && atcBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!isMobile()) { stickyBar.setAttribute('aria-hidden', 'true'); return; }
          stickyBar.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
        });
      }, { threshold: 0.35 }).observe(atcBtn);
    }

    if (!backdrop || !sheetSize || !sheetAdded) return;

    /* ── §4 · sheet plumbing ────────────────────────────────────────── */
    var lastFocus = null;

    function openSheet(el) {
      lastFocus = document.activeElement;
      backdrop.hidden = false;
      el.hidden = false;
      requestAnimationFrame(function () {
        backdrop.classList.add('is-on');
        el.classList.add('is-on');
      });
      var focusable = el.querySelector('button, a[href]');
      if (focusable) safeFocus(focusable);
    }

    function closeSheets() {
      [sheetSize, sheetAdded].forEach(function (el) {
        el.classList.remove('is-on');
        setTimeout(function () { el.hidden = true; el.style.transform = ''; }, 260);
      });
      backdrop.classList.remove('is-on');
      setTimeout(function () { backdrop.hidden = true; }, 260);
      if (lastFocus) { safeFocus(lastFocus); lastFocus = null; }
    }

    backdrop.addEventListener('click', closeSheets);
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && !backdrop.hidden) closeSheets();
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-sheet-close]'), function (b) {
      b.addEventListener('click', closeSheets);
    });

    // drag down to dismiss
    [sheetSize, sheetAdded].forEach(function (el) {
      var y0 = null;
      el.addEventListener('pointerdown', function (e) {
        if (e.target.closest && e.target.closest('button, a')) return;
        y0 = e.clientY;
        try { el.setPointerCapture(e.pointerId); } catch (_e) {}
        el.style.transition = 'none';
      });
      el.addEventListener('pointermove', function (e) {
        if (y0 === null) return;
        el.style.transform = 'translateY(' + Math.max(0, e.clientY - y0) + 'px)';
      });
      el.addEventListener('pointerup', function (e) {
        if (y0 === null) return;
        var dy = Math.max(0, e.clientY - y0);
        el.style.transition = '';
        y0 = null;
        if (dy > 80) closeSheets(); else el.style.transform = 'translateY(0)';
      });
    });

    /* ── §4 · size sheet, built from the variant JSON ───────────────── */
    var sizeWrap = sheetSize.querySelector('[data-pdp-sheet-sizes]');

    function currentColour() {
      var pressed = document.querySelector('[data-pdp-swatch][aria-pressed="true"]');
      return pressed ? pressed.getAttribute('data-option-value') : null;
    }

    // Size axis is option2 when a colour axis exists, option1 otherwise.
    function sizeOf(v) {
      return currentColour() ? v.option2 : v.option1;
    }

    function orderedSizes() {
      var buttons = document.querySelectorAll('[data-pdp-size]');
      var out = [];
      Array.prototype.forEach.call(buttons, function (b) {
        out.push(b.getAttribute('data-option-value'));
      });
      return out;
    }

    function variantFor(size) {
      var colour = currentColour();
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (sizeOf(v) !== size) continue;
        if (colour && v.option1 !== colour) continue;
        return v;
      }
      return null;
    }

    function buildSizeSheet() {
      if (!sizeWrap) return;
      sizeWrap.innerHTML = '';
      orderedSizes().forEach(function (size) {
        var v = variantFor(size);
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = size;
        if (!v || !v.available) b.disabled = true;
        b.addEventListener('click', function () {
          selectSize(size);
          closeSheets();
          heAtcVia = 'size-sheet';   // the sheet is what closed the sale
          setTimeout(submitAdd, 300);
        });
        sizeWrap.appendChild(b);
      });
    }

    // Reuse the panel's own size button so availability, the hidden radio,
    // the price and the label all update through the existing bridge.
    function selectSize(size) {
      var target = document.querySelector('[data-pdp-size][data-option-value="' + size + '"]');
      if (target && !target.disabled) target.click();
    }

    function sizeChosen() {
      return !!document.querySelector('[data-pdp-size][aria-pressed="true"]');
    }

    function hasSizeAxis() {
      return document.querySelectorAll('[data-pdp-size]').length > 0;
    }

    /* ── §4 · add to cart, then the confirmation sheet ──────────────── */
    function submitAdd() {
      if (!form) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }

    function currentVariant() {
      var hidden = form ? form.querySelector('input[name="id"]') : null;
      var id = hidden ? parseInt(hidden.value, 10) : 0;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) return variants[i];
      }
      return null;
    }

    function showAddedSheet(cart) {
      var v = currentVariant();
      var priceEl = sheetAdded.querySelector('[data-pdp-sheet-price]');
      var metaEl = sheetAdded.querySelector('[data-pdp-sheet-meta]');
      var countEl = sheetAdded.querySelector('[data-pdp-sheet-count]');
      var imgEl = sheetAdded.querySelector('[data-pdp-sheet-img]');

      if (priceEl) {
        if (v) {
          var compare = (v.compare_at_price && v.compare_at_price > v.price)
            ? '<s>' + money(v.compare_at_price) + '</s>' : '';
          priceEl.innerHTML = compare + money(v.price);
        } else {
          // Single-variant products (the prints) emit no variants island, so
          // fall back to the price Liquid already rendered into the panel.
          // That row is hidden on mobile but still in the DOM.
          var cur = pdpBuy.querySelector('.buy-panel__price-current');
          var cmp = pdpBuy.querySelector('.buy-panel__price-compare');
          var cmpTxt = (cmp && cmp.style.display !== 'none' && cmp.textContent.trim())
            ? '<s>' + cmp.textContent.trim() + '</s>' : '';
          priceEl.innerHTML = cmpTxt + (cur ? cur.textContent.trim() : '');
        }
      }
      if (metaEl) {
        var bits = [];
        if (v) {
          if (v.option1) bits.push(v.option1);
          if (v.option2) bits.push(v.option2);
        } else {
          var printSize = pdpBuy.querySelector('.print-size .size');
          if (printSize) bits.push(printSize.textContent.trim());
        }
        // addendum 4 §1 · on a Complete Edition the variant options describe
        // the tee only, so the sheet said "Oxblood · S" for a two-piece
        // edition. Name both halves, the print size read from the panel's own
        // "Print size" control — the same snippets/print-size resolver the
        // drawer and /cart render, so the three can never disagree.
        var panelFmtEl = document.querySelector('.pdp-buy[data-format]');
        var isComplete = !!panelFmtEl && panelFmtEl.getAttribute('data-format') === 'complete';
        if (isComplete) {
          var lines = [];
          if (bits.length) lines.push('Tee · ' + bits.join(' / '));
          var cePrint = pdpBuy.querySelector('.print-size .size');
          if (cePrint) lines.push('Print · ' + cePrint.textContent.trim());
          metaEl.textContent = lines.length ? lines.join('\n') : 'One size';
        } else if (panelFmtEl && panelFmtEl.getAttribute('data-format') === 'print') {
          // addendum 5 §1 · name the print here too, so the sheet, the drawer
          // and /cart all say the same thing about the same line.
          metaEl.textContent = bits.length ? 'Print · ' + bits.join(' · ') : 'One size';
        } else {
          metaEl.textContent = bits.length ? bits.join(' · ') : 'One size';
        }
      }
      // addendum 5 §2 · "View bag (1)" counted the add, not the bag: Module E
      // hands over `null` (its /cart/add.js response carries the line, not the
      // cart), so the number stayed at whatever Liquid rendered. The drawer
      // has just been re-rendered from the server at this point, so its own
      // count chip is the fresh truth; /cart.js is the fallback when the
      // drawer is absent or unreadable.
      if (countEl) {
        var known = (cart && typeof cart.item_count === 'number') ? cart.item_count : null;
        if (known === null) {
          var chip = document.querySelector('[data-cart-drawer] [data-cart-count]') ||
                     document.querySelector('[data-cart-count]');
          var parsed = chip ? parseInt((chip.textContent || '').replace(/[^0-9]/g, ''), 10) : NaN;
          if (!isNaN(parsed)) known = parsed;
        }
        if (known !== null) {
          countEl.textContent = String(known);
        } else {
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (c) { if (c && typeof c.item_count === 'number') countEl.textContent = String(c.item_count); })
            .catch(function () {});
        }
      }
      // show the image the gallery is currently on, so the sheet matches
      // the colourway the visitor was looking at
      var activeImg = document.querySelector('.pdp-gallery__media.is-active .pdp-gallery__media-img');
      if (imgEl && activeImg && (activeImg.currentSrc || activeImg.src)) {
        imgEl.src = activeImg.currentSrc || activeImg.src;
      }
      openSheet(sheetAdded);
    }

    // Module E posts to /cart/add.js and then opens the drawer. On mobile we
    // want the sheet instead — this hook lets Module E hand over without
    // either module reaching into the other's internals.
    window.HEPdpMobile.handleAdded = function (cart) {
      if (!isMobile()) return false;
      showAddedSheet(cart);
      return true;
    };

    /* ── §4 · the button itself ─────────────────────────────────────── */
    function onAtcClick(e) {
      if (!isMobile()) return;              // desktop keeps the native submit
      if (!hasSizeAxis() || sizeChosen()) return;
      e.preventDefault();
      e.stopPropagation();
      buildSizeSheet();
      openSheet(sheetSize);
    }

    // B1.3 · "View bag" opens the cart drawer rather than navigating to /cart.
    // The anchor keeps href="/cart" so it still works with JS off, and the
    // drawer is the surface we control.
    var viewBag = sheetAdded.querySelector('[data-pdp-sheet-viewbag]');
    if (viewBag) {
      viewBag.addEventListener('click', function (e) {
        var drawer = document.querySelector('[data-cart-drawer], #CartDrawer');
        if (!drawer || typeof drawer.__open !== 'function') return;   // fall through to /cart
        e.preventDefault();
        closeSheets();
        setTimeout(function () {
          try { drawer.__open(); } catch (err) { window.location.href = '/cart'; }
        }, 280);
      });
    }

    if (atcBtn) atcBtn.addEventListener('click', function (e) {
      heAtcVia = 'panel';
      onAtcClick(e);
    });
    if (stickyBtn) stickyBtn.addEventListener('click', function (e) {
      heAtcVia = 'sticky';
      onAtcClick(e);
    });

    // colour changes can invalidate the chosen size; rebuild the sheet list
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-swatch]'), function (sw) {
      sw.addEventListener('click', function () { setTimeout(buildSizeSheet, 0); });
    });

    buildSizeSheet();
  }

  function init() {
    initMobileMenu();
    initCartDrawer();
    initPdpMobile();
    initVariantSync();
    initCartLine();
    initCompleteTeeSync();
    initBuyPanelAtcAjax();
    initPdpReskinVariantBridge();
    initGiftPanel();
    initCartAutoSwap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

